import React, { useMemo, useState, Component } from 'react';
import { Activity, Filter, Info, Search, ShieldAlert, ShieldCheck, Code2, Database, Ghost, ShieldHalf, Clock, Trash2, Combine, ArrowRight, CheckCircle2, AlertTriangle, Lightbulb, MessageSquare, ExternalLink, ChevronDown, ChevronRight, X } from 'lucide-react';
import { PageLayout } from '../../shared/PageLayout';
import { ConnectionBadge } from '../shared/ConnectionBadge';
import { Card } from '../../../components/ui/Card';
import { Input } from '../../../components/ui/Input';
import { Select } from '../../../components/ui/Select';
import { AdminTabs } from '../shared/AdminTabs';
import { useEscapeKey } from '../../../app/hooks/useEscapeKey';
import { useFocusTrap } from '../../../app/hooks/useFocusTrap';
import { useBodyScrollLock } from '../../../app/hooks/useBodyScrollLock';
import { useGovernanceData } from '../content/governance/hooks/useGovernanceData';
import { computeCodeFrequencyReport, type CodeFrequencyEntry } from '../../../governance/pipeline/utils/codeFrequency';
import { ISSUE_CODES, type IssueDomain, type IssueKind } from '../../../governance/pipeline/issues/codes';
import type { PipelineBatchResult, ValidationIssue } from '../../../governance/pipeline/issues/types';
import { QuestionPreviewPanel } from '../content/questionBank/QuestionPreviewPanel';
import { computeRegistryIntegrity, type RegistryIntegrityProblem, type RegistryIntegritySource } from './codeRegistryIntegrity';
/**
 * Mensvriendelijk label per fantoom-bron in de curatie-drift-banner. Eerder
 * toonde de banner een binaire `relation ? 'relatie' : 'rollup'`, waardoor een
 * transitionele of merge-target-fantoom foutief als "rollup" werd geduid — net
 * de soort stille drift die deze brain-integriteit-laag hoort te ontmaskeren.
 * Deze map dekt élke `RegistryIntegritySource` af, zodat de bron klopt met waar
 * de fantoom-code écht vandaan komt.
 */
const INTEGRITY_SOURCE_LABEL: Record<RegistryIntegritySource, string> = {
  relation: 'relatie',
  'rollup-member': 'rollup',
  transitional: 'transitioneel',
  'transitional-merge-target': 'transitioneel · kan-opgaan-in'
};
// ─── Types & Helpers ──────────────────────────────────────────────────────────
type BucketId = 'all' | 'top' | 'noise' | 'never' | 'rollup' | 'relations' | 'transitional';
/** Hoeveel voorbeeld-vragen per "Laad meer"-stap worden toegevoegd/getoond. */
const EXAMPLE_PAGE_SIZE = 5;
function getCodeDescription(code: string): string {
  // Specifieke codes
  if (code === 'TYPE_MISSING_DATA') return 'Het data-object van de vraag is leeg of ontbreekt volledig. Dit gebeurt vaak als een seed-bestand niet goed is ingevuld.';
  if (code === 'SCHEMA_MISSING_EXPLANATION') return 'De vraag mist een algemene uitlegtekst voor als de leerling het fout heeft. Dit breekt het didactische contract.';
  if (code === 'SCHEMA_MC_TOO_FEW_OPTIONS') return 'Een meerkeuzevraag heeft te weinig opties (meestal minimaal 2 of 3 vereist) om een geldige vraag te vormen.';
  if (code === 'CONTENT_CITO_ISSUE') return 'De Cito-moeilijkheidsgraad of p-waarde komt niet overeen met de verwachte bandbreedte voor deze tier/groep.';
  if (code === 'CONTENT_DISTRACTOR_QUALITY') return 'De afleiders (foute antwoorden) zijn van onvoldoende kwaliteit, bijvoorbeeld te vergelijkbaar of didactisch niet verantwoord. Edtech-noot (SWW didactische ronde): een distractor of error-spotting-val moet op het PARADIGMA van de family blijven. Een fout uit een ander paradigma (bv. een sterk-werkwoord-getalfout in een dubbele-dt-family) leest als taalkundige ruis i.p.v. een leermoment — de leerling oefent één regel, maar struikelt over een ongerelateerde. Validator ongewijzigd; dit is een auteurs-heuristiek die handmatig wordt bewaakt (zie SWW-VT-DUBBEL-006: de reden→reed-val is vervangen door een on-paradigma wacht→wachtte-val). SWW-ronde 3 (28 juni) — dezelfde heuristiek geldt voor categorize/matching-keys: een item-set moet op één onderscheidende as variëren. SWW-LEEN-ENG-KOF-008 sorteerde verledentijdvormen op de kofschip-as (-te vs -de), maar één item ("gameden") stond in het meervoud terwijl de buren ("downloadde", "scoorde", "mailde") enkelvoud waren — die ongevraagde getal-as is taalkundige ruis op een kofschip-oefening. Gecorrigeerd naar de enkelvoudsvorm "gamede"; alle overige uninspecteerde SWW-families (VD-HOMOFOON, TT-INV-TRICK, VT-STERK, VT-ONREGELMATIG, TT-ZIJN-HEBBEN) bleken didactisch zuiver. Validator ongewijzigd.';
  if (code === 'CONTENT_MISSING_RULE_REF') return 'De uitleg noemt de module-specifieke regel niet (bv. kofschip/stam/dt-regel bij SWW) én is niet structureel-didactisch. Edtech-noot (GSR-26): de SWW-onregelmatige/sterke/hulpwerkwoord-families hebben géén kofschip-anker — hun kern is het paradigma (was/waren, ging). De pattern is daarom verbreed met paradigma-vocabulaire zodat die legitieme uitleg niet langer false-positivet.';
  if (code === 'CONTENT_MISSING_CHECK_STRATEGY') return 'De vraag (tier ≥ module-drempel; SWW = 7) mist een controle-strategie-zin ("klopt de tijd? klopt de persoon?"). Edtech-noot (GSR-27): SWW levert die check vaak via solutionSteps ("Check: …") of een rule/strategy-support-aid i.p.v. de uitleg-proza. De check leest nu ook steps + aids, zodat die structureel-geleverde strategie niet langer als "mist" telt.';
  if (code === 'CONTENT_FILL_IN_NARROW') return 'Een invulvraag accepteert weinig (< 4) varianten als goed antwoord. Info-niveau, blokkeert nooit. Op spelling-modules (SWW/SPR) is dit vaak LEGITIEM: één canonieke werkwoordsvorm (zwommen, verwart) is per definitie het enige juiste antwoord — geen synoniemen. Per-module negeren, validator ongewijzigd.';
  if (code === 'DIVERSITY_TYPE_OVERUSE') return 'Binnen deze familie wordt één specifiek vraagtype te vaak gebruikt, wat leidt tot een eenzijdige oefenervaring.';
  if (code === 'SUPPORT_EXPLANATION_QUALITY') return 'De kwaliteit van de stap-voor-stap uitleg of hint is onvoldoende (bijv. te kort, mist opmaak, of bevat geen duidelijke stappen).';
  if (code === 'TYPE_DEPRECATED_INTERACTIVE_CONFIG') return 'Gebruikt een verouderde configuratie voor interactieve elementen. Moet gemigreerd worden naar het nieuwe formaat.';
  // ── Pedagogische kern-codes (DOK / Cito-band / schoolsysteem) ───────────
  // Didactisch precieze omschrijvingen i.p.v. de generieke PEDAGOGY_-fallback,
  // zodat een edtech/Cito-beoordelaar per code de schoolsysteem-betekenis ziet.
  if (code === 'PEDAGOGY_DOK_TOO_LOW') return 'De vraag zit qua denkniveau (Webb DOK) ONDER de vloer van zijn tier-band. Bv. een expert-band (groep 8 plus) vraag die alleen reproductie (DOK 1) vraagt waar toepassen/strategie verwacht wordt. Let op: de vloer is module-bewust — recall-vakken (Topo, spellingregels) en de sub-skills machten/wortels & negatieve getallen mogen legitiem lager.';
  if (code === 'PEDAGOGY_DOK_TOO_HIGH') return 'Tegenhanger (plafond) van DOK_TOO_LOW: de vraag zit qua denkniveau BOVEN wat de tier-band natuurlijk verwacht. Informatief — een diepe vraag op een lage groep is toegestaan, maar de auteur krijgt een seintje om te checken of de vraag niet te zwaar is voor de leerling.';
  if (code === 'CONTENT_BASIS_COGNITIVE_TOO_HIGH') return 'Cito B-band (begin van het leerjaar): de vraag is cognitief te zwaar voor het instapmoment. Hoort bij de B/M/E-opbouw van het schooljaar — een B-vraag moet toegankelijk starten.';
  if (code === 'CONTENT_EIND_COGNITIVE_TOO_LOW') return 'Cito E-band (eind van het leerjaar): de vraag is cognitief te licht voor het eindniveau. Tegenhanger van BASIS_COGNITIVE_TOO_HIGH binnen dezelfde B/M/E-schoolband-logica.';
  if (code === 'CONTENT_PLUS_INADEQUATE') return 'Plus-tier (E8+, boven groep 8): de vraag haalt het verrijkingsniveau niet — te laag denkniveau, te lage Cito-moeilijkheid, of te weinig oplosstappen. Rollup: metadata.reason geeft de precieze tekortkoming. Conceptueel verwant aan DOK_TOO_LOW maar specifiek voor de plus-band.';
  if (code === 'PEDAGOGY_MISCONCEPTION_UNKNOWN') return 'Een afleider verwijst naar een misconceptie-ID die niet in het centrale misconceptie-register staat. Breekt de misconceptie-loop: het systeem kan de denkfout niet herkennen of er gericht op remediëren.';
  if (code === 'PEDAGOGY_MISCONCEPTION_EN_LEGACY') return 'Migratie-teller (info, blokkeert nooit): de misconceptie-ID is nog Engelstalig terwijl Nederlands de standaard is. Telt vanzelf af naarmate IDs worden vernederlandst; pure leenwoord-misconcepties mogen Engels blijven.';
  if (code === 'PEDAGOGY_DISTRACTORS_UNTAGGED' || code === 'PEDAGOGY_DISTRACTORS_PARTIAL') return 'De foute antwoorden zijn niet (volledig) gekoppeld aan een misconceptie. Zonder die koppeling kan de feedback de denkfout niet benoemen en mist de leerling gerichte remediatie — de kern van het didactische contract.';
  if (code === 'SEED_DOK_MISSING') return 'De seed mist een expliciet DOK-niveau (1-4). Zonder DOK kan de pipeline het denkniveau niet tegen de tier-band ijken. De auto-tagger stelt een schatting voor in de metadata.';
  if (code.startsWith('SEED_TIER_')) return 'Tier-validatie: controleert of de vraag in een geldige tier (1-16, oftewel B6 t/m E8+) staat en consistent is met zijn family. Tier = de plek in de groep 6-8 leerlijn.';
  if (code.startsWith('SEED_')) return 'Seed-validatie: controleert de ruwe bronbestanden (tier/skill/sub-skill/DOK) voordat ze in de pipeline worden verwerkt.';
  if (code === 'ASSESSMENT_POOL_CAPACITY') return 'Cito-blueprint haalbaarheid: de vijver toets-geschikte vragen voor een (module, tier-band) is te klein om een betrouwbare toets samen te stellen. Diagnostisch — nooit een blocker.';
  if (code === 'ASSESSMENT_POOL_DISTRIBUTION') return 'Cito-spreiding: de toets-vijver is te eenzijdig (één moeilijkheids-bucket domineert, of te weinig DOK-niveaus). Een goede toets heeft spreiding in p-waarde en denkniveau.';
  if (code === 'ASSESSMENT_POOL_FEASIBILITY') return 'De enige Assessment-Health code die mag blokkeren: een (groep, module) toets-blueprint kan domweg niet gevuld worden. Onmogelijkheid is aantoonbaar, geen mening.';
  if (code.startsWith('ASSESSMENT_')) return 'Macro-governance over de toets-vijver: meet of er per groep/module/band genoeg gespreide, Cito-geschikte vragen zijn voor een betrouwbare toets.';
  if (code === 'GRAPH_CYCLE_DETECTED') return 'De leerlijn-graaf (LDG) bevat een kringverwijzing: family A vereist B en B vereist (via een keten) weer A. Dat is onleerbaar — een leerlijn moet een gerichte volgorde hebben.';
  if (code.startsWith('GRAPH_')) return 'Leerlijn-graaf (LDG) validatie: controleert of de voorkennis-relaties tussen families kloppen, geen kringen vormen en aansluiten op de tier-volgorde van het schoolsysteem.';
  // Prefix fallbacks
  if (code.startsWith('SCHEMA_MC_')) return 'Controleert de structuur van een meerkeuzevraag (bijv. aantal opties, aanwezigheid van een correct antwoord, geen duplicaten).';
  if (code.startsWith('SCHEMA_FILL_IN_')) return 'Controleert de structuur van een invulvraag (bijv. of er geaccepteerde antwoorden zijn gedefinieerd).';
  if (code.startsWith('TYPE_MONEY_')) return 'Geld-specifieke validatie: controleert of de munten/biljetten logisch zijn en of het doelbedrag klopt met de configuratie.';
  if (code.startsWith('TYPE_CLOCK_')) return 'Klok-specifieke validatie: controleert of de uren (0-23) en minuten (0-59) geldig zijn voor analoge of digitale klokken.';
  if (code.startsWith('TYPE_BAR_CHART_') || code.startsWith('TYPE_LINE_CHART_') || code.startsWith('TYPE_PIE_CHART_')) return 'Grafiek-validatie: controleert of de labels, waarden en assen correct zijn gedefinieerd en of er genoeg data is om de grafiek te tekenen.';
  if (code.startsWith('TYPE_ORDERING_')) return 'Ordeningsvraag-validatie: controleert of de te ordenen items correct zijn en de verwachte volgorde logisch is.';
  if (code.startsWith('TYPE_MATCHING_')) return 'Combineervraag-validatie: controleert of de linker- en rechterkolom correct aan elkaar gekoppeld kunnen worden.';
  if (code.startsWith('CONTENT_')) return 'Inhoudelijke kwaliteitscontrole: kijkt verder dan de structuur naar de didactische waarde van de vraag en antwoorden.';
  if (code.startsWith('PEDAGOGY_')) return 'Pedagogische controle: valideert het DOK-niveau, de aansluiting bij het leerdoel en de afhandeling van misconcepties.';
  if (code.startsWith('VISUAL_')) return 'Visuele controle: verifieert of afbeeldingen, diagrammen en andere visuele hulpmiddelen correct zijn geconfigureerd en renderbaar zijn.';
  if (code.startsWith('SUPPORT_')) return 'Ondersteuningscontrole: valideert de aanwezigheid en kwaliteit van hints, stappenplannen en feedback.';
  if (code.startsWith('DIVERSITY_')) return 'Diversiteitscontrole: waarborgt dat leerlingen een gevarieerde mix van vraagtypes en interacties krijgen binnen een leerdoel.';
  if (code.startsWith('SEED_')) return 'Seed-validatie: controleert de ruwe bronbestanden op structurele fouten voordat ze in de pipeline worden verwerkt.';
  if (code.startsWith('SCHEMA_')) return 'Basis schema-validatie: controleert of alle verplichte velden (zoals ID, module, tekst) aanwezig zijn en het juiste type hebben.';
  if (code.startsWith('TYPE_')) return 'Vraagtype-specifieke validatie: controleert de payload-data die specifiek is voor dit ene interactieve vraagtype.';
  return 'Specifieke validatie voor dit domein. Zorgt ervoor dat de data voldoet aan de vereisten voor de renderer of de engine.';
}
/**
 * DIDACTISCHE LENS (28 juni) — edtech/Cito/didactisch/PO-pass over de codes.
 *
 * De §-audit (GOVERNANCE_CODES_AUDIT_2026-06) keek STRUCTUREEL (dood/levend/
 * ruis/naam-rollup). Deze laag kijkt CONCEPTUEEL: welke codes vormen een
 * floor/ceiling-PAAR (twee kanten van dezelfde didactische as — beide mogen
 * legitiem bestaan, samenvoegen zou de as platslaan), en welke vormen een
 * THEMATISCH CLUSTER (overlappen in betekenis maar mogen NIET mergen omdat ze
 * in verschillende stages zitten of een ander signaal dragen). Read-only:
 * maakt de relatie zichtbaar op de kaart, verandert geen pipeline-gedrag.
 */
// 'pair'    = floor/ceiling op één as (complementair — beide legitiem)
// 'cluster' = thematisch verwant over stages heen (verwant, niet mergen)
// 'tension' = BOTSEND — twee codes/signalen die op dezelfde vraag tegengesteld
//             kunnen trekken, zodat een auteur ze niet allebei kan bevredigen.
//             Dit beantwoordt direct de "welke zijn botsend?"-vraag.
type DidacticRelationKind = 'pair' | 'cluster' | 'tension';
interface DidacticRelation {
  kind: DidacticRelationKind;
  /** Korte naam van de as/het thema. */
  label: string;
  /** De codes die in deze relatie samenhangen. */
  members: string[];
  /** Edtech/PO-duiding: waarom ze samenhangen én waarom NIET samenvoegen. */
  note: string;
}
const DIDACTIC_RELATIONS: DidacticRelation[] = [{
  kind: 'pair',
  label: 'DOK-as: vloer ↔ plafond',
  members: ['PEDAGOGY_DOK_TOO_LOW', 'PEDAGOGY_DOK_TOO_HIGH'],
  note: 'Twee kanten van dezelfde denkniveau-as (Webb DOK) t.o.v. de tier-band. TOO_LOW = onder de vloer (te makkelijk voor de groep), TOO_HIGH = boven het plafond (mogelijk te zwaar). Bewust géén één code: ze sturen tegengestelde auteur-acties. De vloer is module-bewust (recall-vakken + machten/wortels & negatieve getallen mogen lager).'
}, {
  kind: 'pair',
  label: 'Cito B/M/E-band: instap te zwaar ↔ eind te licht',
  members: ['CONTENT_BASIS_COGNITIVE_TOO_HIGH', 'CONTENT_EIND_COGNITIVE_TOO_LOW'],
  note: 'Spiegelpaar op de B/M/E-schooljaaras. BASIS_TOO_HIGH bewaakt dat een begin-vraag toegankelijk start; EIND_TOO_LOW dat een eind-vraag het niveau haalt. Samen bewaken ze de cognitieve opbouw binnen één leerjaar — niet samenvoegen, ze meten tegengestelde uiteinden.'
}, {
  kind: 'cluster',
  label: 'Thema "te makkelijk voor het eindniveau"',
  members: ['PEDAGOGY_DOK_TOO_LOW', 'CONTENT_EIND_COGNITIVE_TOO_LOW', 'CONTENT_PLUS_INADEQUATE'],
  note: 'Drie codes die uit verschillende hoeken "te licht voor het beoogde niveau" signaleren: DOK_TOO_LOW (denkniveau vs tier-band), EIND_COGNITIVE_TOO_LOW (Cito E-band), PLUS_INADEQUATE (verrijking E8+). NIET mergen — ze zitten in aparte stages (pedagogy/content) en gebruiken andere bronsignalen. Wél als één PO-thema te lezen: "haalt de vraag het eindniveau?".'
}, {
  kind: 'cluster',
  label: 'Thema "misconceptie-koppeling / didactisch contract"',
  members: ['PEDAGOGY_DISTRACTORS_UNTAGGED', 'PEDAGOGY_DISTRACTORS_PARTIAL', 'PEDAGOGY_MISCONCEPTION_UNKNOWN', 'PEDAGOGY_MISCONCEPTION_EN_LEGACY'],
  note: 'Alle vier raken de misconceptie-loop: zijn de afleiders gekoppeld (UNTAGGED/PARTIAL), bestaat de koppeling in het register (UNKNOWN), en is de ID-taal correct (EN_LEGACY). Verschillende ernst/kind — samen vormen ze de gezondheid van het "fout → gerichte feedback"-contract, maar elk vraagt een eigen actie.'
}, {
  kind: 'cluster',
  label: 'Thema "Cito-psychometrie & toets-vijver"',
  members: ['CONTENT_CITO_ISSUE', 'SUPPORT_FAMILY_CITO_SPREAD', 'ASSESSMENT_POOL_CAPACITY', 'ASSESSMENT_POOL_DISTRIBUTION', 'ASSESSMENT_POOL_FEASIBILITY'],
  note: 'De Cito-laag: per vraag (p-waarde/discriminatie), per family (spreiding), en per toets-vijver (capaciteit/spreiding/haalbaarheid). Samen bepalen ze of een betrouwbare, gespreide toets samengesteld kan worden. Aparte schaalniveaus (vraag → family → pool) — bewust gescheiden codes.'
},
// ── BOTSEND (tension) — twee echte spanningen uit de systeem-audits ────────
{
  kind: 'tension',
  label: 'Botsing: DOK-vloer ↔ module-bewuste recall-snap',
  members: ['PEDAGOGY_DOK_TOO_LOW', 'SEED_DOK_MISSING'],
  note: 'BOTSEND op recall-vakken (Topo, spellingregels). De DOK-vloer (PEDAGOGY_DOK_TOO_LOW) wil minimaal toepassen/strategie zien, maar voor recall-knowledge/comprehension-modules is DOK 1 per definitie correct — cognitiveSignals snapt het denkniveau dáár omlaag en SEED_DOK_MISSING wordt zelfs gedegradeerd naar INFO. Een auteur op een recall-module kan dus niet "én DOK omhoog én vakgetrouw" zijn. Bron: GOVERNANCE_SYSTEM_HARDENING_AUDIT_2026-06 + COGNITIVE_SIGNALS.md. NIET oplossen door één code te schrappen — de spanning is ECHT en moet module-bewust blijven; de vloer is al gekalibreerd, dit is een leesbaar-maken van waar ze tegen elkaar in kunnen vuren.'
}, {
  kind: 'tension',
  label: 'Botsing: Cito-band (p-waarde) ↔ IRT-moeilijkheidsindex',
  members: ['CONTENT_CITO_ISSUE', 'CONTENT_BASIS_COGNITIVE_TOO_HIGH'],
  note: 'BOTSENDE semantiek: de tier-labels zijn Cito (p-waarde: hoger = makkelijker), maar de seed-`citoDifficulty` is een IRT-style index (hoger = moeilijker) — twee tegengestelde "Cito"-frames in één systeem (STRATEGIC_VALIDATION_LDG_TIER_CONTENT_2026). Daardoor kan CONTENT_CITO_ISSUE (band-mismatch) en CONTENT_BASIS_COGNITIVE_TOO_HIGH (instap te zwaar) op dezelfde vraag tegengesteld lezen: de ene meet de psychometrische band, de andere de cognitieve instaplast. Niet samenvoegen — eerst de frame-ambiguïteit oplossen (één moeilijkheids-semantiek), dán pas overwegen.'
}];
/** Hoeveel relaties expliciet BOTSEND zijn — gebruikt in het tab-label zodat
 *  de "welke zijn botsend?"-vraag al vanaf de tab-rij zichtbaar is. */
const TENSION_COUNT = DIDACTIC_RELATIONS.filter(r => r.kind === 'tension').length;
function getDidacticRelation(code: string): DidacticRelation | null {
  return DIDACTIC_RELATIONS.find(r => r.members.includes(code)) ?? null;
}
function getCriticalAnalysis(entry: CodeRegistryEntry): string | null {
  if (entry.isConsolidationCandidate) {
    return 'Kritische noot: Deze code is gemarkeerd als legacy/stub, maar heeft nog steeds een levend emit-pad. Omdat hij nooit vuurt, betekent dit dat onze data schoon is. Voeg deze code samen met een generieke variant (zoals TYPE_MISSING_DATA) in plaats van hem blindelings te verwijderen. Verwijderen zonder de onderliggende check te behouden, verlaagt onze veiligheidsmarge.';
  }
  if (entry.suspectedNoise) {
    if (entry.noiseReason === 'high-volume') {
      return 'Kritische noot: Deze code vuurt bij meer dan 20% van de vragen, maar is GEEN blocker of high-severity coach — daarom als ruis gemarkeerd. Dit wijst op een structureel patroon in onze contentcreatie of een te strenge validator. Overweeg of deze regel een echte fout signaleert, of dat we de validator moeten versoepelen om "waarschuwings-moeheid" bij auteurs te voorkomen. (Let op: een high-volume code die WÉL een blocker of ≥REVIEW-coach is, telt bewust NIET als ruis — hoge reikwijdte + hoge impact = systeem-prioriteit, geen ruis.)';
    }
    return 'Kritische noot: Deze code is een "info"-melding of zit op TRACE-severity — een diagnostisch signaal dat nooit blokkeert. Laag-impact; behoud als breadcrumb, maar weeg hem niet mee als kwaliteitsfout.';
  }
  if (entry.code.includes('LENGTH_MISMATCH')) {
    return 'Kritische noot: We hebben momenteel 6+ verschillende codes voor "array lengtes komen niet overeen" (voor tabellen, grafieken, etc.). Deze delen exact dezelfde onderliggende logica. Dit is een sterke kandidaat voor de TYPE_ARRAY_LENGTH_MISMATCH rollup, wat de pipeline-code aanzienlijk zou vereenvoudigen.';
  }
  if (entry.code.includes('TOO_FEW')) {
    return 'Kritische noot: Er zijn veel varianten van "te weinig elementen". Let op: sommige vallen onder SCHEMA (zoals MC opties) en andere onder TYPE (zoals grafiek staven). Cross-stage samenvoegen is riskant, maar per stage kunnen we deze consolideren.';
  }
  if (entry.code === 'CONTENT_MISSING_RULE_REF' || entry.code === 'CONTENT_MISSING_CHECK_STRATEGY') {
    return 'Kritische noot (edtech/SWW-audit): deze high-volume content-coach had een aantoonbare false-positive-subklasse, geen achterlopende redactie. De validator las alléén feedback.explanationText, terwijl SWW de regel/controlestrategie structureel levert via solutionSteps ("Check: …") en rule/verbTable-support-aids. GSR-26/GSR-27 verbreedden de check om óók steps + aids te lezen (en paradigma-vocabulaire voor onregelmatige/sterke families). Emit-pad blijft actief: uitleg die nergens — proza, steps of aid — de regel/controle noemt, vuurt terecht. SWW-didactiek-pass (28 juni, ronde 2): VT-ZWAK-KOF, VD-KOF, TT-STAM, TT-DSTAM, VT-VZFS, VD-ZONDER-GE, VD-STERK en VT-ZIJN-HEBBEN handmatig gecontroleerd op begrijpelijkheid, scaffolding-kwaliteit, distractor-misconcepties en DOK-balans — bevonden didactisch gezond (kofschip/BEVO-HEG/v-f/z-s glossen aanwezig, controlestrategieën expliciet, recall-items legitiem DOK 1, alle sww.*-misconceptie-IDs geregistreerd). Geen registry-herkalibratie nodig op deze families; één taaltechnische seed-fix (zelfverwijzende context "spelletje spelletjes-quiz" → leesbare zin). Meet de volgende run of het restant legitiem-schaars is; zo niet → nieuwe kritische pass, niet de regex verder verwateren.';
  }
  if (entry.lifecycle === 'dead') {
    return 'Kritische noot: Gemarkeerd als dood. Voordat we de code definitief verwijderen, is een grep op de codebase vereist om te garanderen dat er nergens meer een `emit(ISSUE_CODES.' + entry.code + ')` staat.';
  }
  return null;
}
/**
 * Vertrouwen in een voorgestelde rollup-merge (zie GOVERNANCE_CODES_AUDIT_
 * 2026-06 §9). De audit (§2 🟡 + §3) eist shadow-first bewijs vóór élke merge,
 * dus dit is een VOORSTEL-niveau, geen "doe dit nu":
 *   - 'high'   🟢 zuiver structureel, identieke remediatie, lage twijfel
 *   - 'medium' 🟡 plausibel maar vereist per-code verificatie van kind/stage
 *   - 'review' 🔴 naam-gelijkenis maar semantisch risico — eerst RFC
 */
type RollupConfidence = 'high' | 'medium' | 'review';
/**
 * Een voorgestelde rollup-familie: een set bestaande codes die "hetzelfde
 * doen" en — net als de al-bestaande rollups (CONTENT_CITO_ISSUE,
 * DIVERSITY_TYPE_OVERUSE, …) — samengevouwen kunnen worden onder één
 * canonieke code met een `metadata.reason`-discriminator. Dit is een
 * read-only ANALYSE-laag: de pagina voert geen merge uit, ze maakt de
 * kandidaten zichtbaar zodat een mens de shadow-first stap kan starten.
 */
interface RollupCandidateFamily {
  id: string;
  /** Voorgestelde canonieke code (mag al bestaan, bv. TYPE_MISSING_DATA). */
  proposedCode: string;
  title: string;
  /** Wat de leden gemeen hebben — de "doet hetzelfde"-kern. */
  sharedConcern: string;
  /** De `metadata.reason`-discriminator die het verlies aan precisie opvangt. */
  reasonDiscriminator: string;
  confidence: RollupConfidence;
  /** Caveat / waarom het (nog) geen merge is — de eerlijkheid uit de audit. */
  caveat: string;
  /** De bestaande codes die in deze rollup zouden opgaan. */
  memberCodes: string[];
}
/**
 * De rollup-kandidaten, afgeleid uit een handmatige scan van codes.ts tegen
 * het al-bewezen rollup-patroon (Sprint 6–8). Elke familie deelt één concern
 * en verschilt alleen in een discriminator — exact de vorm die
 * CONTENT_DISTRACTOR_QUALITY c.s. al gebruiken. Volgorde = afnemend vertrouwen.
 *
 * BELANGRIJK: dit is een voorstel-catalogus, geen uitgevoerde merge. Conform
 * audit §3 mag een merge pas ná: (1) grep op elke emit-call, (2) shadow-run
 * met dubbel-emit, (3) 100% overlap-bewijs — en in series van 5–7.
 */
const ROLLUP_CANDIDATE_FAMILIES: RollupCandidateFamily[] = [{
  id: 'length-mismatch',
  proposedCode: 'TYPE_ARRAY_LENGTH_MISMATCH',
  title: 'Array-lengte komt niet overeen',
  sharedConcern: 'Twee parallelle arrays die even lang MOETEN zijn (rij vs. headers, waarden vs. labels, items vs. correcte volgorde) zijn dat niet. Zuiver structureel — identieke remediatie ongeacht het vraagtype.',
  reasonDiscriminator: "reason ∈ { 'ordering' | 'table_row' | 'schedule_row' | 'bar_chart' | 'line_chart' | 'pie_chart' }",
  confidence: 'high',
  caveat: 'De audit (§2 🟡) noemt dit de plausibelste kandidaat. Enige eis: metadata moet rijk genoeg blijven om de twee arrays bij naam te noemen, anders verliest de admin-diagnose context.',
  memberCodes: ['TYPE_ORDERING_LENGTH_MISMATCH', 'TYPE_TABLE_ROW_LENGTH_MISMATCH', 'TYPE_SCHEDULE_ROW_LENGTH_MISMATCH', 'TYPE_BAR_CHART_LENGTH_MISMATCH', 'TYPE_LINE_CHART_LENGTH_MISMATCH', 'TYPE_PIE_CHART_LENGTH_MISMATCH']
}, {
  id: 'slider-bounds',
  proposedCode: 'TYPE_SLIDER_BOUNDS_INVALID',
  title: 'Slider min/max-grenzen ongeldig',
  sharedConcern: 'estimation_slider en thermometer_slider valideren WOORD-VOOR-WOORD dezelfde grenscondities (geen min, geen max, min ≥ max). Twee parallelle code-paden voor één concept.',
  reasonDiscriminator: "reason ∈ { 'no_min' | 'no_max' | 'min_gte_max' } + metadata.slider ∈ { 'estimation' | 'thermometer' }",
  confidence: 'high',
  caveat: 'Let op: dit is een MERGE van logica, niet alleen van codes — de twee validators (mathValidators/interactiveValidators) zouden naar één gedeelde grenscheck moeten. Pure structurele consistentie-check, geen didactisch verlies.',
  memberCodes: ['TYPE_EST_SLIDER_NO_MIN', 'TYPE_EST_SLIDER_NO_MAX', 'TYPE_EST_SLIDER_MIN_GTE_MAX', 'TYPE_THERM_NO_MIN', 'TYPE_THERM_NO_MAX', 'TYPE_THERM_MIN_GTE_MAX']
}, {
  id: 'no-data',
  proposedCode: 'TYPE_MISSING_DATA',
  title: 'Vraagtype-payload volledig leeg',
  sharedConcern: 'Het hele data-object van een gespecialiseerd type ontbreekt of is leeg — de "⚠️ Configuratie ontbreekt"-renderfallback. De generieke TYPE_MISSING_DATA bestaat al; deze per-type *_NO_DATA-varianten doen er een dubbele.',
  reasonDiscriminator: 'metadata.questionType draagt al het type — geen aparte reason nodig; TYPE_MISSING_DATA bestaat al als kandidaat-doel',
  confidence: 'medium',
  caveat: 'Verifieer eerst dat geen renderer-fallback op de SPECIFIEKE code rekent (bv. een andere amber-card per type). De vier TYPE_*_INVALID_CONFIG-stubs (audit §6.5) horen hier conceptueel ook bij, maar emitteren nog — alleen meten.',
  memberCodes: ['TYPE_BAR_CHART_NO_DATA', 'TYPE_LINE_CHART_NO_DATA', 'TYPE_TEXT_HIGHLIGHT_NO_DATA', 'TYPE_MATCHING_NO_DATA']
}, {
  id: 'too-few',
  proposedCode: 'TYPE_TOO_FEW_ELEMENTS',
  title: 'Te weinig elementen voor het vraagtype',
  sharedConcern: 'Een collectie (opties, paren, slices, categorieën, statements, segmenten, …) heeft minder leden dan het minimum dat het vraagtype rendeerbaar/beantwoordbaar maakt. Allemaal "telling < drempel".',
  reasonDiscriminator: "reason = de collectie-soort (bv. 'mc_options' | 'matching_pairs' | 'pie_slices' | 'categories' | 'tf_statements' | …) + metadata.min/actual",
  confidence: 'review',
  caveat: 'Open vraag uit de audit (§2 🟡): SCHEMA_MC_TOO_FEW_OPTIONS zit in de SCHEMA-stage, de rest in TYPE. Cross-stage mergen breekt de IssueDomain-mapping. Waarschijnlijk PER STAGE consolideren (één schema-rollup + één type-rollup), niet over stages heen.',
  memberCodes: ['SCHEMA_MC_TOO_FEW_OPTIONS', 'TYPE_MATCHING_TOO_FEW', 'TYPE_BAR_CHART_TOO_FEW', 'TYPE_PIE_CHART_TOO_FEW_SLICES', 'TYPE_TRUE_FALSE_TOO_FEW', 'TYPE_CATEGORIZE_TOO_FEW_CATEGORIES', 'TYPE_CATEGORIZE_TOO_FEW_ITEMS', 'TYPE_COMPARISON_TOO_FEW_ITEMS', 'TYPE_SENT_ERROR_TOO_FEW', 'TYPE_TEXT_HIGHLIGHT_TOO_FEW_SEGMENTS', 'TYPE_NUMBER_SEQ_TOO_SHORT']
}];
const ROLLUP_CONFIDENCE_CONFIG: Record<RollupConfidence, {
  dot: string;
  label: string;
  badge: string;
  icon: React.ReactNode;
}> = {
  high: {
    dot: '🟢',
    label: 'Hoog vertrouwen',
    badge: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    icon: <CheckCircle2 className="w-3 h-3" data-id="element-6371" data-mp="1jxdo8rxgk76c" />
  },
  medium: {
    dot: '🟡',
    label: 'Plausibel',
    badge: 'bg-amber-50 text-amber-700 border-amber-200',
    icon: <Clock className="w-3 h-3" data-id="element-6372" data-mp="2qp5v4n725kqw" />
  },
  review: {
    dot: '🔴',
    label: 'Eerst RFC',
    badge: 'bg-red-50 text-red-700 border-red-200',
    icon: <AlertTriangle className="w-3 h-3" data-id="element-6373" data-mp="2vnh5cz19lx9n" />
  }
};
/**
 * TRANSITIONEEL / SUNSET (28 juni — antwoord op de "oude codes als EN_LEGACY"-
 * vraag). Een aparte CURATIE-klasse, los van levenscyclus (vangnet/latent/dead)
 * en los van ruis. Het gaat hier om codes die NU logisch zijn maar hun
 * meerwaarde VERLIEZEN zodra een eindige migratie/opschoning klaar is —
 * `PEDAGOGY_MISCONCEPTION_EN_LEGACY` is het kanonieke voorbeeld (telt af naar 0
 * naarmate IDs vernederlandst worden). De live-teller is hier het sunset-
 * signaal: zolang hij groter dan 0 is loopt de migratie nog; staat hij
 * (stabiel) op 0, dan is de code een retirement-kandidaat.
 *
 * Drie soorten:
 *   - 'migration-counter' : eindige teller die naar 0 zakt en daarna leeg blijft
 *                           (de migratie is "af"). Bij 0 = sunset-kandidaat.
 *   - 'autocorrect-drift' : load-time auto-correctie die alleen vuurt zolang
 *                           seed-JSON afwijkt van de taxonomie-SSOT. Verdwijnt
 *                           permanent zodra de bron is uitgelijnd (geen stille
 *                           correctie meer nodig).
 *   - 'deprecation'       : signaleert een verouderd veld/pad dat uitgefaseerd
 *                           wordt. Sunset = pas verwijderen als het deprecated
 *                           pad uit de renderers/seeds verdwenen is.
 *
 * Read-only: dit verandert geen pipeline-gedrag of severity. Het maakt de
 * "kan weg / kan samengevoegd zodra migratie af is"-status zichtbaar zodat een
 * mens de retirement-stap kan plannen (grep + snapshot-scan vóór verwijderen).
 */
type TransitionalKind = 'migration-counter' | 'autocorrect-drift' | 'deprecation';
interface TransitionalCode {
  code: string;
  kind: TransitionalKind;
  /** Korte naam van de migratie/deprecatie. */
  label: string;
  /** Waarom deze code NU logisch is. */
  rationaleNow: string;
  /** Wanneer hij zijn meerwaarde verliest — de sunset-conditie. */
  sunsetCondition: string;
  /** Wat er daarna mee moet gebeuren (retire / merge / regex verwijderen). */
  retirementGuidance: string;
  /** Optioneel: code waarin hij conceptueel kan opgaan i.p.v. los te retiren. */
  mergeTarget?: string;
}
/**
 * De transitionele codes, handmatig gecureerd uit een scan van codes.ts +
 * de migratie-trackers (MISCONCEPTION_REGISTRY_SEED_SYNC_2026 §6,
 * GOVERNANCE_VALIDATION_TRIAGE_2026-06, GET/STU SSOT-audits). Volgorde =
 * dichtst bij sunset eerst.
 */
const TRANSITIONAL_CODES: TransitionalCode[] = [{
  code: 'PEDAGOGY_MISCONCEPTION_EN_LEGACY',
  kind: 'migration-counter',
  label: 'EN→NL misconceptie-ID migratie',
  rationaleNow: 'Nederlands is de canonieke taal voor misconceptie-IDs (SSOT §3). Tijdens de migratie signaleert deze info-teller élke nog-Engelse ID, zodat de §6-teller niet stiekem kan groeien terwijl de backlog wordt opgeruimd.',
  sunsetCondition: 'De stream staat (stabiel, minstens 1 sprint) op 0 én de loanword-uitzonderingen zijn naar een eigen allowlist-registry verplaatst. Dan is élke niet-leenwoord-ID vernederlandst en heeft de teller geen werk meer.',
  retirementGuidance: 'Retire de code (grafsteen-comment in codes.ts + grep op de emit in pedagogyStage.ts), of degradeer tot pure loanword-allowlist-guard. NIET mergen — hij meet een unieke taal-as die geen andere code dekt.'
}, {
  code: 'SEED_TIER_AUTOCORRECTED',
  kind: 'autocorrect-drift',
  label: 'Tier-drift t.o.v. taxonomie-SSOT',
  rationaleNow: 'De seedConsolidator overschrijft een tier in de seed-JSON die niet matcht met de taxonomie en vuurt deze blocker, zodat de stille load-time-correctie zichtbaar blijft (N6: never silent in productie).',
  sunsetCondition: 'Alle seed-JSON heeft een tier/levelCode die exact matcht met taxonomy/data/<MOD>.ts — dan is er niets meer te corrigeren. GET/STU zijn al gesloten (25 juni); het restant is een eindige opruim.',
  retirementGuidance: 'NIET de code verwijderen — het is een levend vangnet tegen toekomstige drift. "Sunset" betekent hier: teller naar 0 drijven via seed-fixes, dan blijft hij staan als stille invariant (verschuift naar 🟢 levend vangnet in de levenscyclus-tab).'
}, {
  code: 'SEED_SKILL_AUTOCORRECTED',
  kind: 'autocorrect-drift',
  label: 'Skill-drift t.o.v. taxonomie-SSOT',
  rationaleNow: 'Coach-signaal: de seed-skill matchte niet met de taxonomie en is bij load gecorrigeerd. Houdt drift tussen seed-authoring en de SSOT zichtbaar.',
  sunsetCondition: 'Alle seeds dragen de canonieke skill uit de taxonomie. Net als de tier-variant een eindige uitlijn-klus, geen blijvende kwaliteitsfout.',
  retirementGuidance: 'Zelfde lijn als SEED_TIER_AUTOCORRECTED: teller naar 0 via seed-fixes, code blijft als stille invariant. Sterke kandidaat om mét de subSkill-variant onder één SEED_TAXONOMY_AUTOCORRECTED-rollup te vouwen (metadata.reason in skill | subSkill).',
  mergeTarget: 'SEED_SUBSKILL_AUTOCORRECTED'
}, {
  code: 'SEED_SUBSKILL_AUTOCORRECTED',
  kind: 'autocorrect-drift',
  label: 'Sub-skill-drift t.o.v. taxonomie-SSOT',
  rationaleNow: 'Coach-signaal: de seed-subSkill matchte niet met de taxonomie en is bij load gecorrigeerd — identieke logica als de skill-variant, alleen een niveau dieper.',
  sunsetCondition: 'Alle seeds dragen de canonieke sub-skill. Eindige uitlijn-klus tegen de SSOT.',
  retirementGuidance: 'Conceptueel één met SEED_SKILL_AUTOCORRECTED: beide controleren "taxonomie-veld niet gelijk aan SSOT → auto-fix". Kandidaat voor een SEED_TAXONOMY_AUTOCORRECTED-rollup (reason = het veld). Pas mergen ná shadow-first dubbel-emit zoals de audit eist.',
  mergeTarget: 'SEED_SKILL_AUTOCORRECTED'
}, {
  code: 'TYPE_DEPRECATED_INTERACTIVE_CONFIG',
  kind: 'deprecation',
  label: 'Verouderd interactiveConfig-formaat',
  rationaleNow: 'Blocker die vuurt zodra een seed het deprecated interactiveConfig-veld gebruikt i.p.v. het nieuwe per-type data.{type}-namespace. Begeleidt de migratie naar het moderne payload-formaat.',
  sunsetCondition: 'Geen enkele seed gebruikt interactiveConfig meer (teller stabiel 0) én de renderers hebben geen fallback-pad meer dat het oude veld leest.',
  retirementGuidance: 'Al gemarkeerd als 🟡 consolidatie-kandidaat: kan opgaan in de generieke TYPE_MISSING_DATA (de "config ontbreekt/ongeldig"-familie). Mergen óf retiren ná grep dat geen renderer nog op de specifieke code rekent.',
  mergeTarget: 'TYPE_MISSING_DATA'
}, {
  code: 'PEDAGOGY_REGEX_FALLBACK',
  kind: 'deprecation',
  label: 'Regex-parsing fallback in renderers',
  rationaleNow: 'Coach (severity 40), blocker (70) op tier groter-gelijk 9. Signaleert dat een vraag terugvalt op fragiele regex-parsing van de stam omdat gestructureerde data ontbreekt — het deprecated pad dat strict-mode wil uitfaseren.',
  sunsetCondition: 'Alle (tier-relevante) vragen leveren gestructureerde data en de renderers hebben hun regex-fallback verwijderd (PEDAGOGICAL_CONTRACT_SPEC strict-mode stap 5).',
  retirementGuidance: 'Sunset is hier code-werk, geen teller-naar-0: pas retiren als de renderer-regex weg is. Tot dan blijft hij de enige bewaker van het deprecated pad — NIET vroegtijdig verwijderen.'
}];
const TRANSITIONAL_KIND_CONFIG: Record<TransitionalKind, {
  dot: string;
  label: string;
  badge: string;
  icon: React.ReactNode;
}> = {
  'migration-counter': {
    dot: '⏳',
    label: 'Migratie-teller',
    badge: 'bg-violet-50 text-violet-700 border-violet-200',
    icon: <Clock className="w-3 h-3" data-id="element-6374" data-mp="2d1nnoqb3nzuv" />
  },
  'autocorrect-drift': {
    dot: '🔧',
    label: 'Auto-correctie-drift',
    badge: 'bg-cyan-50 text-cyan-700 border-cyan-200',
    icon: <Combine className="w-3 h-3" data-id="element-6375" data-mp="2xvwb2xv9bwwi" />
  },
  deprecation: {
    dot: '🚧',
    label: 'Deprecatie',
    badge: 'bg-orange-50 text-orange-700 border-orange-200',
    icon: <AlertTriangle className="w-3 h-3" data-id="element-6376" data-mp="32wtq51kz0ats" />
  }
};
function getTransitional(code: string): TransitionalCode | null {
  return TRANSITIONAL_CODES.find(t => t.code === code) ?? null;
}
/**
 * Lifecycle status for a never-fired code (zie GOVERNANCE_CODES_AUDIT_2026-06
 * §6.2). Een nooit-getriggerde code is NIET per definitie dood — de meeste
 * zijn gezonde vangnetten/invarianten die niet vuren omdat de seeds correct
 * zijn. We splitsen ze in drie:
 *   - 'safety-net' (🟢): heeft (vrijwel zeker) een levend emit-pad — een
 *      schema-/type-guard die niet vuurt omdat de data schoon is. Behouden.
 *   - 'latent'     (🟡): emit-pad zit achter een feature/vraagtype dat (nog)
 *      niet in de huidige seeds voorkomt. Behouden + noteren.
 *   - 'dead'       (🔴): expliciet legacy/stub-gemarkeerd in codes.ts; sterke
 *      kandidaat voor opschoning — MOET nog met een grep op de emit-call
 *      bevestigd worden vóór verwijdering.
 * Voor gevuurde codes is de status 'fired'.
 */
type LifecycleStatus = 'fired' | 'safety-net' | 'latent' | 'dead';
interface CodeRegistryEntry {
  code: string;
  domain: IssueDomain | 'unknown';
  kind: IssueKind | 'unknown';
  maxSeverity: number;
  count: number;
  questionsAffected: number;
  suspectedNoise: boolean;
  /**
   * Waarom `suspectedNoise` zo resolved — onderscheidt low-impact ruis
   * (info/trace) van het high-volume-geval, zodat de kritische noot niet
   * langer een high-volume blocker als "ruis" duidt (false-positive fix).
   */
  noiseReason: 'info-kind' | 'trace' | 'high-volume' | 'none';
  reasonBreakdown?: {
    reason: string;
    count: number;
    percentOfCode: number;
  }[];
  isNeverFired: boolean;
  lifecycle: LifecycleStatus;
  /**
   * True voor de 🟡-codes die codes.ts als stub/legacy markeert MAAR die nog
   * een levend emit-pad hebben (audit §6.5). Krijgt een afwijkende kaart-blurb
   * zodat de beheerder ziet: "levend, alleen consolidatie-onderzoek" i.p.v. de
   * generieke "wacht op content"-latent-tekst.
   */
  isConsolidationCandidate?: boolean;
}
/**
 * Leid het domein af uit de code-prefix. Dit is de provisionele bron voor
 * NOOIT-getriggerde codes (gevuurde codes krijgen hun echte `domain` uit de
 * live frequency-report). De prefix→domein-afbeelding MOET in de pas lopen met
 * de canonieke `IssueDomain`-union in `issues/codes.ts` — bij drift belanden
 * codes stilletjes in 'unknown' en daarmee in de verkeerde levenscyclus-bucket.
 *
 * Noten bij de mapping:
 *   - GRAPH_  → 'graph'  (LDG/prerequisite-stage; eerder ontbrekend → viel
 *     foutief in 'unknown' → 'latent').
 *   - familyStats: er bestaat GEEN `FAMILYSTATS_`-prefix; die codes dragen de
 *     `SUPPORT_FAMILY_*`-prefix en vallen dus (bewust, historisch) onder
 *     'support'. Ze vuren in de praktijk altijd, dus de live `domain` overstemt
 *     deze afleiding sowieso.
 *   - scoring: geen `SCORING_`-prefix in de catalogus → geen mapping nodig.
 */
function inferDomain(code: string): IssueDomain | 'unknown' {
  if (code.startsWith('SCHEMA_')) return 'schema';
  if (code.startsWith('TYPE_')) return 'type';
  if (code.startsWith('GRAPH_')) return 'graph';
  if (code.startsWith('CONTENT_')) return 'content';
  if (code.startsWith('PEDAGOGY_')) return 'pedagogy';
  if (code.startsWith('VISUAL_')) return 'visual';
  if (code.startsWith('SUPPORT_')) return 'support';
  if (code.startsWith('DIVERSITY_')) return 'diversity';
  if (code.startsWith('SEED_')) return 'seed';
  if (code.startsWith('ASSESSMENT_')) return 'assessmentPool';
  if (code.startsWith('SUBVALIDATOR_')) return 'gate';
  return 'unknown';
}
/**
 * 🔴 ECHT DOOD — codes waarvan met een grep is bevestigd dat ze GEEN enkele
 * emit-call meer hebben in `src/governance/pipeline/stages/**` of
 * `builders.ts` (audit GOVERNANCE_CODES_AUDIT_2026-06 §3, stap 2). Dit is een
 * handmatig onderhouden allowlist; een code komt hier ALLEEN in ná die
 * grep-bevestiging. De heuristiek gokt NOOIT "dood" — bij twijfel valt een
 * code terug op 🟢/🟡.
 *
 * STATUS (28 juni 2026): LEEG. De kandidaten die intuïtief "stub/legacy"
 * leken — de vier `TYPE_*_INVALID_CONFIG`-stubs en
 * `TYPE_DEPRECATED_INTERACTIVE_CONFIG` — blijken bij grep een LEVEND emit-pad
 * te hebben (`mathValidators.ts` resp. `typeStage.ts`). Ze vuren niet omdat de
 * seeds correct/niet-deprecated zijn — exact het kwaliteitssignaal. Ze horen
 * dus NIET in 🔴; ze zijn hooguit 🟡 consolidatie-kandidaten ("alleen meten,
 * niet verwijderen", audit §2 🟢). Geen enkele code is op dit moment
 * grep-bewezen dood.
 */
const KNOWN_DEAD_CODES = new Set<string>([
  // (leeg — zie comment hierboven; vul alleen ná grep-bevestiging van géén emit-call)
]);
/**
 * 🟡 CONSOLIDATIE-KANDIDATEN — codes die codes.ts als tijdelijk/stub of legacy
 * markeert en die de audit (§2 🟢) als opschoon-kandidaat noemt, MAAR die nog
 * een levend emit-pad hebben. Ze zijn NIET dood: ze vuren niet omdat de data
 * schoon/niet-deprecated is. Beleid: alleen meten, niet verwijderen tot een
 * grep + snapshot-scan nul emits aantoont. We tonen ze als 🟡 latent zodat ze
 * zichtbaar blijven als opruim-onderzoek, niet als dode code.
 */
const CONSOLIDATION_CANDIDATE_CODES = new Set<string>([
// Stub-validators, in codes.ts gemarkeerd als "Ruis-audit v1.6 T2.2"
// (kandidaat voor één generieke TYPE_MISSING_DATA — maar emitteren nog).
'TYPE_SQUARE_ROOT_BUILDER_INVALID_CONFIG', 'TYPE_PLACE_VALUE_BUILD_INVALID_CONFIG', 'TYPE_BAR_MODEL_PART_INVALID_CONFIG', 'TYPE_STRATEGY_SOLVER_INVALID_CONFIG',
// Expliciet legacy-gemarkeerde interactive-config code (emit in typeStage.ts).
'TYPE_DEPRECATED_INTERACTIVE_CONFIG']);
/**
 * 🟢 LEVENDE VANGNETTEN — domeinen waarvan een nooit-getriggerde code vrijwel
 * zeker een actief emit-pad heeft (structurele invariant die niet vuurt omdat
 * de data schoon is). SCHEMA_/TYPE_-guards vallen hier standaard onder.
 */
const SAFETY_NET_DOMAINS = new Set<IssueDomain | 'unknown'>(['schema', 'type']);
/**
 * Classificeer een nooit-getriggerde code in 🟢/🟡/🔴. Dit is een
 * PROVISIONELE classificatie op basis van naam/domein-prefix — geen
 * runtime-bewijs van levend/dood. Alleen 🔴 leidt tot opschoning, en die stap
 * vereist altijd nog een grep-bevestiging op de emit-call (audit §3 stap 2).
 */
function classifyNeverFired(code: string, domain: IssueDomain | 'unknown'): LifecycleStatus {
  if (KNOWN_DEAD_CODES.has(code)) return 'dead';
  // Stub/legacy-codes met een nog-levend emit-pad → expliciet 🟡 (meten, niet
  // opruimen) — NIET 🟢, zodat ze als consolidatie-onderzoek zichtbaar blijven.
  if (CONSOLIDATION_CANDIDATE_CODES.has(code)) return 'latent';
  // SUBVALIDATOR_CRASHED is een gate-vangnet dat alleen vuurt als een
  // sub-validator daadwerkelijk crasht — gezond dat 'm stil is, dus 🟢.
  if (SAFETY_NET_DOMAINS.has(domain) || code === 'SUBVALIDATOR_CRASHED') return 'safety-net';
  // GRAPH_/VISUAL_/SUPPORT_/PEDAGOGY_/SEED_/DIVERSITY_ etc.: emit-pad hangt
  // vaak achter een feature/vraagtype/registry-conditie die (nog) niet in de
  // huidige seeds voorkomt → latent vangnet.
  return 'latent';
}
const LIFECYCLE_CONFIG: Record<Exclude<LifecycleStatus, 'fired'>, {
  dot: string;
  label: string;
  badge: string;
  icon: React.ReactNode;
  blurb: string;
}> = {
  'safety-net': {
    dot: '🟢',
    label: 'Levend vangnet',
    badge: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    icon: <ShieldHalf className="w-3 h-3" data-id="element-6377" data-mp="3h2y271coonx8" />,
    blurb: 'Structurele invariant met een actief emit-pad. Vuurt niet omdat de seeds correct zijn — dat is het kwaliteitssignaal. Behouden.'
  },
  latent: {
    dot: '🟡',
    label: 'Latent',
    badge: 'bg-amber-50 text-amber-700 border-amber-200',
    icon: <Clock className="w-3 h-3" data-id="element-6378" data-mp="1rqmfy0k08mnc" />,
    blurb: 'Emit-pad zit achter een feature/vraagtype dat (nog) niet in de seeds voorkomt. Vuurt zodra die content er is. Behouden + noteren.'
  },
  dead: {
    dot: '🔴',
    label: 'Opruim-kandidaat',
    badge: 'bg-red-50 text-red-700 border-red-200',
    icon: <Trash2 className="w-3 h-3" data-id="element-6379" data-mp="2u4aarp6rdvux" />,
    blurb: 'In codes.ts als legacy/stub gemarkeerd. Sterke opschoon-kandidaat — bevestig eerst met een grep op de emit-call, ruim dan op met grafsteen-comment.'
  }
};
const DOMAIN_COLORS: Record<string, string> = {
  schema: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  type: 'bg-blue-50 text-blue-700 border-blue-200',
  content: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  pedagogy: 'bg-amber-50 text-amber-700 border-amber-200',
  visual: 'bg-purple-50 text-purple-700 border-purple-200',
  support: 'bg-rose-50 text-rose-700 border-rose-200',
  diversity: 'bg-teal-50 text-teal-700 border-teal-200',
  seed: 'bg-cyan-50 text-cyan-700 border-cyan-200',
  assessmentPool: 'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200',
  gate: 'bg-gray-50 text-gray-700 border-gray-200',
  unknown: 'bg-gray-50 text-gray-700 border-gray-200'
};
const KIND_CONFIG: Record<string, {
  icon: React.ReactNode;
  color: string;
  bg: string;
}> = {
  blocker: {
    icon: <ShieldAlert className="w-3.5 h-3.5" data-id="element-6380" data-mp="o5h5bpfr7pz9" />,
    color: 'text-red-700',
    bg: 'bg-red-50 border-red-200'
  },
  coach: {
    icon: <ShieldCheck className="w-3.5 h-3.5" data-id="element-6381" data-mp="e946ye99kny6" />,
    color: 'text-amber-700',
    bg: 'bg-amber-50 border-amber-200'
  },
  info: {
    icon: <Info className="w-3.5 h-3.5" data-id="element-6382" data-mp="h7fmcff6i2u" />,
    color: 'text-blue-700',
    bg: 'bg-blue-50 border-blue-200'
  },
  unknown: {
    icon: <Ghost className="w-3.5 h-3.5" data-id="element-6383" data-mp="5asgcc8edq5q" />,
    color: 'text-gray-500',
    bg: 'bg-gray-50 border-gray-200'
  }
};
// ─── Main Component ───────────────────────────────────────────────────────────
export function CodeRegistryPage() {
  const {
    batchResult,
    isRunning
  } = useGovernanceData();
  const [activeBucket, setActiveBucket] = useState<BucketId>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [domainFilter, setDomainFilter] = useState<string>('all');
  const [lifecycleFilter, setLifecycleFilter] = useState<LifecycleStatus | 'all'>('all');
  // 1. Build the full registry list by joining static ISSUE_CODES with live
  //    frequency. De Code Registry is in de eerste plaats een CATALOGUS: de
  //    code-universe (ISSUE_CODES) en de levenscyclus-analyse (vangnet/latent/
  //    dead) zijn statisch bekend, los van een pipeline-run. Daarom bouwen we
  //    de volledige catalogus óók zonder batchResult — alle codes verschijnen
  //    dan als "nooit getriggerd" (er is simpelweg nog geen run om tegen te
  //    meten). Alleen de frequentie/voorbeeld-vragen zijn echt run-afhankelijk.
  const registryEntries = useMemo<CodeRegistryEntry[]>(() => {
    // Zonder run: lege frequency-map → elke code valt in de never-fired tak.
    const freqMap = new Map<string, CodeFrequencyEntry>(batchResult ? computeCodeFrequencyReport(batchResult, {
      topN: 10000
    }).entries.map(e => [e.code, e] as const) : []);
    const allCodes = Object.values(ISSUE_CODES);
    const entries: CodeRegistryEntry[] = allCodes.map(code => {
      const live = freqMap.get(code);
      if (live) {
        return {
          code,
          domain: live.domain,
          kind: live.kind,
          maxSeverity: live.maxSeverity,
          count: live.count,
          questionsAffected: live.questionsAffected,
          suspectedNoise: live.suspectedNoise,
          noiseReason: live.noiseReason,
          reasonBreakdown: live.reasonBreakdown,
          isNeverFired: false,
          lifecycle: 'fired' as LifecycleStatus
        };
      } else {
        const domain = inferDomain(code);
        return {
          code,
          domain,
          kind: 'unknown' as const,
          maxSeverity: 0,
          count: 0,
          questionsAffected: 0,
          suspectedNoise: false,
          noiseReason: 'none' as const,
          isNeverFired: true,
          lifecycle: classifyNeverFired(code, domain),
          isConsolidationCandidate: CONSOLIDATION_CANDIDATE_CODES.has(code)
        };
      }
    });
    // Sort: Fired (desc by count) -> Never Fired (alpha)
    return entries.sort((a, b) => {
      if (a.isNeverFired && !b.isNeverFired) return 1;
      if (!a.isNeverFired && b.isNeverFired) return -1;
      if (!a.isNeverFired && !b.isNeverFired) return b.count - a.count;
      return a.code.localeCompare(b.code);
    });
  }, [batchResult]);
  // 2. Filter entries based on active bucket, search, and domain
  const filteredEntries = useMemo(() => {
    return registryEntries.filter(entry => {
      // Bucket filter
      if (activeBucket === 'top' && (entry.isNeverFired || entry.suspectedNoise)) return false;
      if (activeBucket === 'noise' && !entry.suspectedNoise) return false;
      if (activeBucket === 'never' && !entry.isNeverFired) return false;
      // Lifecycle filter (only meaningful within the "Nooit Getriggerd" bucket)
      if (activeBucket === 'never' && lifecycleFilter !== 'all' && entry.lifecycle !== lifecycleFilter) return false;
      // Domain filter
      if (domainFilter !== 'all' && entry.domain !== domainFilter) return false;
      // Search filter
      if (searchQuery && !entry.code.toLowerCase().includes(searchQuery.toLowerCase())) return false;
      return true;
    });
  }, [registryEntries, activeBucket, searchQuery, domainFilter, lifecycleFilter]);
  // Lifecycle breakdown of the never-fired universe (🟢 / 🟡 / 🔴)
  const lifecycleStats = useMemo(() => {
    let safetyNet = 0;
    let latent = 0;
    let dead = 0;
    registryEntries.forEach(e => {
      if (!e.isNeverFired) return;
      if (e.lifecycle === 'safety-net') safetyNet++;else if (e.lifecycle === 'latent') latent++;else if (e.lifecycle === 'dead') dead++;
    });
    return {
      safetyNet,
      latent,
      dead
    };
  }, [registryEntries]);
  // 3. Compute stats for tabs
  const stats = useMemo(() => {
    let top = 0;
    let noise = 0;
    let never = 0;
    registryEntries.forEach(e => {
      if (e.isNeverFired) never++;else if (e.suspectedNoise) noise++;else top++;
    });
    return {
      all: registryEntries.length,
      top,
      noise,
      never
    };
  }, [registryEntries]);
  const TABS = [{
    id: 'all',
    label: `Alle Codes (${stats.all})`,
    icon: <Database className="w-4 h-4" data-id="element-6384" data-mp="3rjfuaqt2815t" />
  }, {
    id: 'top',
    label: `Actief & Signaal (${stats.top})`,
    icon: <Activity className="w-4 h-4" data-id="element-6385" data-mp="efzd14ywk8r" />
  }, {
    id: 'noise',
    label: `Ruis / Low-Impact (${stats.noise})`,
    icon: <Filter className="w-4 h-4" data-id="element-6386" data-mp="5bpwz42ig27o" />
  }, {
    id: 'never',
    label: `Nooit Getriggerd (${stats.never})`,
    icon: <Ghost className="w-4 h-4" data-id="element-6387" data-mp="22743dcpfrbpl" />
  }, {
    id: 'rollup',
    label: `Rollup-kandidaten (${ROLLUP_CANDIDATE_FAMILIES.length})`,
    icon: <Combine className="w-4 h-4" data-id="element-6388" data-mp="o9rov90rsmh7" />
  }, {
    id: 'relations',
    label: `Relaties & Botsingen (${TENSION_COUNT} botsend)`,
    icon: <AlertTriangle className="w-4 h-4 text-red-600" data-id="element-6389" data-mp="jg6pbcjj3kfw" />
  }, {
    id: 'transitional',
    label: `Transitioneel / Sunset (${TRANSITIONAL_CODES.length})`,
    icon: <Clock className="w-4 h-4" data-id="element-6390" data-mp="aiwfzwq9u5dm" />
  }];
  const uniqueDomains = useMemo(() => {
    const domains = new Set(registryEntries.map(e => e.domain));
    return Array.from(domains).sort();
  }, [registryEntries]);
  // Live emission count per code (0 when never-fired) — fed to the rollup view
  // so each proposed member shows how often it actually fires today.
  const liveCountForCode = useMemo(() => {
    const m = new Map<string, number>();
    registryEntries.forEach(e => m.set(e.code, e.count));
    return (code: string) => m.get(code) ?? 0;
  }, [registryEntries]);
  // Of er al een pipeline-run is om de frequentie tegen te meten. Zonder run
  // staat de catalogus er wél (alle codes als "nooit getriggerd"), maar dan
  // betekent dat "nog niet gemeten" — NIET "schoon". De banner maakt dat
  // verschil expliciet zodat de telling niet misleidt.
  const hasRun = !!batchResult;
  const integrityProblems = useMemo<RegistryIntegrityProblem[]>(() => computeRegistryIntegrity(DIDACTIC_RELATIONS, ROLLUP_CANDIDATE_FAMILIES, TRANSITIONAL_CODES), []);
  return <PageLayout title="Code Registry" subtitle="Gedetailleerd overzicht van alle governance issue-codes, hun frequentie, legacy-status en transitionele/sunset-codes." actions={<ConnectionBadge status={isRunning ? 'loading' : 'live'} data-id="element-6392" data-mp="1fex5fid1wki5" />} breadcrumbs={[{
    label: 'Inhoud'
  }, {
    label: 'Code Registry'
  }]} data-id="element-6391" data-mp="1pb8f00l9aw8w">
      {/* ── Brain-integriteit: fantoom-code-drift in de gecureerde kaarten ── */}
      {integrityProblems.length > 0 ? <Card className="p-4 mb-5 bg-red-50/60 border-red-300" data-id="element-6393" data-mp="1fk44x3cbazq">
          <div className="flex items-start gap-3" data-id="element-6394" data-mp="1dqxviwn2ss9">
            <ShieldAlert className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" data-id="element-6395" data-mp="3smejlzyski0c" />
            <div className="min-w-0" data-id="element-6396" data-mp="aga1444c77f1">
              <h3 className="text-sm font-bold text-gray-900" data-id="element-6397" data-mp="af755hs5qes9">
                Curatie-drift: {integrityProblems.length} fantoom-
                {integrityProblems.length === 1 ? 'referentie' : 'referenties'}
              </h3>
              <p className="text-[13px] text-gray-700 mt-1 leading-relaxed" data-id="element-6398" data-mp="3dx8p2pawffys">
                De gecureerde relatie-/rollup-kaarten verwijzen naar codes die
                niet (meer) in de canonieke{' '}
                <span className="font-mono text-[11px]" data-id="element-6399" data-mp="3nov6x4hqsdai">ISSUE_CODES</span>
                -set bestaan. De hersenen tonen dan een kaart die naar een
                niet-bestaande code wijst — herstel de naam in{' '}
                <span className="font-mono text-[11px]" data-id="element-6400" data-mp="2q7ebg5ir9ndh">codes.ts</span> of in de
                betreffende relatie/rollup.
              </p>
              <ul className="mt-2 space-y-1" data-id="element-6401" data-mp="u38a4oqd8qnb">
                {integrityProblems.map(p => <li key={`${p.source}:${p.code}:${p.context}`} className="flex items-center gap-2 text-xs" data-id="element-6402" data-mp="z2b2p9p5t4ee">
                    <span className="font-mono font-bold text-red-700 bg-white border border-red-200 rounded px-1.5 py-0.5 text-[11px]" data-id="element-6403" data-mp="18sw8pi75xb7c">
                      {p.code}
                    </span>
                    <span className="text-gray-500" data-id="element-6404" data-mp="g48jjbjam1oi">
                      {INTEGRITY_SOURCE_LABEL[p.source] ?? p.source} ·{' '}
                      {p.context}
                    </span>
                  </li>)}
              </ul>
            </div>
          </div>
        </Card> : <Card className="p-3 mb-5 bg-emerald-50/40 border-emerald-100" data-id="element-6405" data-mp="1wj0arl4pd2w8">
          <div className="flex items-center gap-2.5" data-id="element-6406" data-mp="1wl30s55uuw0y">
            <ShieldCheck className="w-4 h-4 text-emerald-600 flex-shrink-0" data-id="element-6407" data-mp="20jak97rpebfu" />
            <p className="text-[12px] text-gray-600 leading-relaxed" data-id="element-6408" data-mp="3l2w3ap3iwp3d">
              <span className="font-semibold text-gray-800" data-id="element-6409" data-mp="3l47akyw6gez8">
                Curatie-integriteit OK.
              </span>{' '}
              Alle codes in de relatie- en rollup-kaarten bestaan nog in de
              canonieke{' '}
              <span className="font-mono text-[11px]" data-id="element-6410" data-mp="37brbuaab7344">ISSUE_CODES</span>-set —
              de hersenen wijzen nergens naar een fantoom-code.
            </p>
          </div>
        </Card>}

      {/* ── Pre-run notice: catalogus staat er, maar er is nog niet gemeten ── */}
      {!hasRun && <Card className="p-4 mb-5 bg-amber-50/50 border-amber-200" data-id="element-6411" data-mp="2taewo12z35ja">
          <div className="flex items-start gap-3" data-id="element-6412" data-mp="2oc3mtqcogv12">
            <Info className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" data-id="element-6413" data-mp="2odezygs0jf4u" />
            <div className="min-w-0" data-id="element-6414" data-mp="rk3g2i717gmw">
              <h3 className="text-sm font-bold text-gray-900" data-id="element-6415" data-mp="rj248bi6yq44">
                Nog geen pipeline-run gemeten
              </h3>
              <p className="text-[13px] text-gray-600 mt-1 leading-relaxed" data-id="element-6416" data-mp="3rksi4e6k6r79">
                Je ziet de volledige{' '}
                <span className="font-medium" data-id="element-6417" data-mp="ezgtonf6msl">catalogus</span> van{' '}
                <span className="font-semibold tabular-nums" data-id="element-6418" data-mp="gahvgphdgg0">{stats.all}</span>{' '}
                geregistreerde codes met hun statische
                levenscyclus-classificatie. Omdat er nog geen run is, staan alle
                codes als <span className="font-medium" data-id="element-6419" data-mp="38trjxsd9izor">nooit getriggerd</span>{' '}
                — dat betekent hier{' '}
                <span className="font-medium" data-id="element-6420" data-mp="3cwxnutcwkes2">“nog niet gemeten”</span>, niet
                “schoon”. Start een governance-run om frequenties en
                voorbeeld-vragen per code te zien.
              </p>
            </div>
          </div>
        </Card>}

      {/* ── Summary Header ── */}
      <Card className="p-5 mb-5" data-id="element-6421" data-mp="x5s00v5nguyl">
        <div className="flex items-start gap-4" data-id="element-6422" data-mp="x2nmh76aomv9">
          <div className="w-10 h-10 rounded-xl bg-gray-900 text-white flex items-center justify-center flex-shrink-0" data-id="element-6423" data-mp="124374y4k8rd4">
            <Code2 className="w-5 h-5" data-id="element-6424" data-mp="2mmnhghmu6um3" />
          </div>
          <div className="min-w-0" data-id="element-6425" data-mp="2hn14m9ax7gdt">
            <h2 className="text-lg font-bold text-gray-900 leading-tight" data-id="element-6426" data-mp="2hnu5wur41dok">
              Issue-Code Gezondheid
            </h2>
            <p className="text-sm text-gray-600 mt-1 leading-relaxed" data-id="element-6427" data-mp="2wd00u6ix0hqn">
              Van de{' '}
              <span className="font-semibold text-gray-900 tabular-nums" data-id="element-6428" data-mp="2rjnp2wzc45fb">
                {stats.all}
              </span>{' '}
              geregistreerde codes zijn er{' '}
              <span className="font-semibold text-gray-900 tabular-nums" data-id="element-6429" data-mp="30ch54wmlv8u8">
                {stats.never}
              </span>{' '}
              {hasRun ? 'nooit getriggerd in de huidige dataset. ' : 'nog niet gemeten (geen run). '}
              {hasRun && <>
                  <span className="font-semibold text-gray-900 tabular-nums" data-id="element-6430" data-mp="1kx1u49b7m7sl">
                    {stats.noise}
                  </span>{' '}
                  codes produceren voornamelijk ruis (info/TRACE-niveau, of
                  &gt;20% van alle vragen zónder blocker/≥REVIEW-impact — een
                  high-volume blocker telt bewust níét als ruis).
                </>}
            </p>
            <p className="text-[13px] text-gray-500 mt-2 leading-relaxed" data-id="element-6431" data-mp="3irsj2jfprg3x">
              <span className="font-semibold text-gray-700" data-id="element-6432" data-mp="3dus8bv7x5p6h">
                Nooit-getriggerd betekent niet dood.
              </span>{' '}
              De meeste zijn gezonde vangnetten/invarianten die niet vuren omdat
              de seeds correct zijn — dat is juist een kwaliteitssignaal. De{' '}
              <span className="text-emerald-700 font-medium" data-id="element-6433" data-mp="303nh9hima57r">
                🟢 {lifecycleStats.safetyNet} levende vangnetten
              </span>{' '}
              en{' '}
              <span className="text-amber-700 font-medium" data-id="element-6434" data-mp="fb24hg62fuqm">
                🟡 {lifecycleStats.latent} latente
              </span>{' '}
              codes blijven staan.{' '}
              {lifecycleStats.dead === 0 ? <>
                  Er zijn{' '}
                  <span className="text-red-700 font-medium" data-id="element-6435" data-mp="mv30huq6ygtt">
                    🔴 0 grep-bewezen dode codes
                  </span>{' '}
                  — de stub-/legacy-codes die intuïtief opruimbaar leken (zoals
                  de{' '}
                  <span className="font-mono text-[11px]" data-id="element-6436" data-mp="cxouyq5t3cga">
                    TYPE_*_INVALID_CONFIG
                  </span>
                  -stubs) blijken een levend emit-pad te hebben en staan daarom
                  als 🟡 consolidatie-onderzoek, niet als dode code.
                </> : <>
                  Alleen de{' '}
                  <span className="text-red-700 font-medium" data-id="element-6437" data-mp="vvj371z41jxm">
                    🔴 {lifecycleStats.dead} opruim-kandidaten
                  </span>{' '}
                  zijn echte opschoning — en die vragen eerst een
                  grep-bevestiging op hun emit-call.
                </>}
            </p>
          </div>
        </div>
      </Card>

      {/* ── Filters & Tabs ── */}
      <div className="mb-5 space-y-4" data-id="element-6438" data-mp="1u676tvtf860g">
        <AdminTabs tabs={TABS} activeTab={activeBucket} wrapOnDesktop onTabChange={id => {
        setActiveBucket(id as BucketId);
        // Lifecycle sub-filter only applies inside the "never" bucket.
        if (id !== 'never') setLifecycleFilter('all');
      }} data-id="element-6439" data-mp="1u78palcrnus7" />

        {/* Lifecycle sub-filter — only within the Nooit Getriggerd bucket */}
        {activeBucket === 'never' && <div className="rounded-xl border border-gray-200 bg-gray-50/60 p-4" data-id="element-6440" data-mp="16gmqzlskdove">
            <div className="flex items-start gap-2 mb-3" data-id="element-6441" data-mp="11i1r5nh8577h">
              <Info className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" data-id="element-6442" data-mp="11jmkj9rsyk3r" />
              <p className="text-xs text-gray-500 leading-relaxed" data-id="element-6443" data-mp="whx75nh3xuci">
                Levenscyclus van de nooit-getriggerde codes. Deze indeling is{' '}
                <span className="font-medium text-gray-700" data-id="element-6444" data-mp="rm861fpfw5e3">provisioneel</span>{' '}
                (op basis van domein/naam) — alleen 🔴 leidt tot opschoning, en
                die stap vereist eerst een grep-bevestiging op de emit-call.
              </p>
            </div>
            <div className="flex flex-wrap gap-2" data-id="element-6445" data-mp="rewbkjxzefq1">
              <LifecycleChip active={lifecycleFilter === 'all'} onClick={() => setLifecycleFilter('all')} label={`Alle (${stats.never})`} data-id="element-6446" data-mp="wdr4wftkrmoe" />
              <LifecycleChip active={lifecycleFilter === 'safety-net'} onClick={() => setLifecycleFilter('safety-net')} label={`🟢 Levende vangnetten (${lifecycleStats.safetyNet})`} tone="emerald" data-id="element-6447" data-mp="3nqy2bfijziw" />
              <LifecycleChip active={lifecycleFilter === 'latent'} onClick={() => setLifecycleFilter('latent')} label={`🟡 Latent (${lifecycleStats.latent})`} tone="amber" data-id="element-6448" data-mp="3c4dg9cty5ab8" />
              <LifecycleChip active={lifecycleFilter === 'dead'} onClick={() => setLifecycleFilter('dead')} label={`🔴 Opruim-kandidaat (${lifecycleStats.dead})`} tone="red" data-id="element-6449" data-mp="1mnrkj1qruvnm" />
            </div>
            {lifecycleFilter !== 'all' && <p className="text-[11px] text-gray-500 mt-3 leading-relaxed" data-id="element-6450" data-mp="1rl01iz06otna">
                {LIFECYCLE_CONFIG[lifecycleFilter].blurb}
              </p>}
          </div>}

        {/* Search + domain filter — not meaningful in the rollup / relations / transitional views */}
        {activeBucket !== 'rollup' && activeBucket !== 'relations' && activeBucket !== 'transitional' && <div className="flex flex-col sm:flex-row gap-3" data-id="element-6451" data-mp="kxuw1foqn9wk">
              <div className="flex-1" data-id="element-6452" data-mp="kyeagmf6jg5u">
                <Input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Zoek op code naam..." icon={<Search className="w-4 h-4" data-id="element-6454" data-mp="2wlref8hqrtx0" />} variant="admin" size="sm" data-id="element-6453" data-mp="fz1ftfi2h4sp" />
              </div>
              <Select value={domainFilter} onChange={e => setDomainFilter(e.target.value)} options={[{
          value: 'all',
          label: 'Alle domeinen'
        }, ...uniqueDomains.map(d => ({
          value: d,
          label: d
        }))]} variant="admin" size="sm" fullWidth={false} data-id="element-6455" data-mp="2hw1xo4x61hbm" />
            </div>}
      </div>

      {activeBucket === 'rollup' ? <RollupCandidatesView liveCountFor={liveCountForCode} data-id="element-6456" data-mp="3t9e2oj4nw5zd" /> : activeBucket === 'relations' ? <DidacticRelationsView liveCountFor={liveCountForCode} data-id="element-6457" data-mp="22tj1kgxs0cd" /> : activeBucket === 'transitional' ? <TransitionalSunsetView liveCountFor={liveCountForCode} hasRun={hasRun} data-id="element-6458" data-mp="3rubfzutj8eol" /> /* ── Registry List ── */ : <div className="space-y-3" data-id="element-6459" data-mp="3mw05rizbs25u">
          {filteredEntries.length === 0 ? <div className="text-center py-12 bg-white rounded-xl border border-gray-200" data-id="element-6460" data-mp="3iuesvxmsjnvn">
              <Ghost className="w-8 h-8 text-gray-300 mx-auto mb-3" data-id="element-6461" data-mp="jerp32gc38cp" />
              <p className="text-sm text-gray-500" data-id="element-6462" data-mp="eff28hpgq1a4">Geen codes gevonden.</p>
            </div> : filteredEntries.map(entry => <CodeEntryCard key={entry.code} entry={entry} batchResult={batchResult} data-id="element-6463" data-mp="9ndrx1buukmq" />)}
        </div>}
    </PageLayout>;
}
// ─── Entry Card ───────────────────────────────────────────────────────────────
function CodeEntryCard({
  entry,
  batchResult
}: {
  entry: CodeRegistryEntry;
  batchResult: PipelineBatchResult | null;
}) {
  const [expanded, setExpanded] = useState(false);
  const [previewQuestionId, setPreviewQuestionId] = useState<string | null>(null);
  // Hoeveel voorbeeld-vragen tonen we. Start op 5; "Laad meer" verhoogt dit
  // met EXAMPLE_PAGE_SIZE tot alle getroffen vragen zichtbaar zijn.
  const [visibleExamples, setVisibleExamples] = useState(EXAMPLE_PAGE_SIZE);
  const kcfg = KIND_CONFIG[entry.kind] || KIND_CONFIG.unknown;
  const dcolor = DOMAIN_COLORS[entry.domain] || DOMAIN_COLORS.unknown;
  const lcfg = entry.lifecycle !== 'fired' ? LIFECYCLE_CONFIG[entry.lifecycle] : null;
  // Consolidatie-kandidaten (§6.5) zijn 🟡, maar krijgen een afwijkende
  // rationale: ze hebben een LEVEND emit-pad — niet "wacht op content".
  const lifecycleBlurb = lcfg && entry.isConsolidationCandidate ? 'Levend emit-pad (in codes.ts als stub/legacy gemarkeerd). Vuurt niet omdat de seeds correcte config hebben en niemand het deprecated veld nog gebruikt — dat is het kwaliteitssignaal. Alleen meten, niet verwijderen: pas opruimen ná een grep + snapshot-scan die nul historische emits aantoont.' : lcfg?.blurb;
  const examples = useMemo(() => {
    if (!expanded || !batchResult || entry.isNeverFired) return [];
    const found: {
      questionId: string;
      familyId: string;
      issue: ValidationIssue;
    }[] = [];
    for (const res of batchResult.results) {
      const issues = res.gate.issues.filter(i => i.code === entry.code);
      for (const issue of issues) {
        found.push({
          questionId: res.questionId,
          familyId: res.familyId,
          issue
        });
        if (found.length >= visibleExamples) return found;
      }
    }
    return found;
  }, [expanded, batchResult, entry.code, entry.isNeverFired, visibleExamples]);
  // Zijn er nog meer voorbeelden dan we nu tonen?
  const hasMoreExamples = examples.length < entry.questionsAffected;
  const description = getCodeDescription(entry.code);
  const criticalAnalysis = getCriticalAnalysis(entry);
  const didacticRelation = getDidacticRelation(entry.code);
  const transitional = getTransitional(entry.code);
  return <Card className={`transition-colors overflow-hidden ${entry.lifecycle === 'dead' ? 'bg-red-50/30 border-red-100' : entry.isNeverFired ? 'bg-gray-50/50 border-gray-200' : entry.suspectedNoise ? 'bg-amber-50/30 border-amber-100' : 'bg-white border-gray-200'}`} data-id="element-6464" data-mp="1745di4m15qkw">
      <div className="p-4 cursor-pointer hover:bg-gray-50/50 transition-colors" onClick={() => {
      setExpanded(prev => {
        // Bij inklappen het voorbeeld-venster resetten zodat heropenen
        // weer met de eerste batch begint.
        if (prev) setVisibleExamples(EXAMPLE_PAGE_SIZE);
        return !prev;
      });
    }} data-id="element-6465" data-mp="1zqq618e8s8q1">
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-4" data-id="element-6466" data-mp="ee59s939mjjm">
          {/* Left: Identity */}
          <div className="min-w-0 flex-1" data-id="element-6467" data-mp="jc6ueygcaqx7">
            <div className="flex items-center gap-2 flex-wrap mb-1.5" data-id="element-6468" data-mp="3d2n4fg8ol27g">
              <h3 className="text-sm font-bold text-gray-900 font-mono break-all" data-id="element-6469" data-mp="3h50901boyu2n">
                {entry.code}
              </h3>
              {entry.isNeverFired && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-gray-200 text-gray-600 text-[10px] font-bold uppercase tracking-wider" data-id="element-6470" data-mp="3m2jxdu23sn8b">
                  <Ghost className="w-3 h-3" data-id="element-6471" data-mp="2th0hgz6k2zn0" />
                  Nooit getriggerd
                </span>}
              {lcfg && <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-bold uppercase tracking-wider ${lcfg.badge}`} title={lifecycleBlurb} data-id="element-6472" data-mp="33dvfzi2tx01a">
                  {lcfg.icon}
                  {entry.isConsolidationCandidate ? 'Consolidatie-kandidaat' : lcfg.label}
                </span>}
              {entry.suspectedNoise && !entry.isNeverFired && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 text-[10px] font-bold uppercase tracking-wider" data-id="element-6473" data-mp="235p0ek3dyuzu">
                  <Filter className="w-3 h-3" data-id="element-6474" data-mp="1y51017t9p1sy" />
                  Ruis
                </span>}
              {didacticRelation && <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-bold uppercase tracking-wider ${didacticRelation.kind === 'tension' ? 'border-red-200 bg-red-50 text-red-700' : 'border-indigo-200 bg-indigo-50 text-indigo-700'}`} title={`${didacticRelation.kind === 'pair' ? 'Didactisch paar' : didacticRelation.kind === 'tension' ? 'Botsende codes' : 'Didactisch cluster'}: ${didacticRelation.label}`} data-id="element-6475" data-mp="1p5nom9ll235l">
                  {didacticRelation.kind === 'tension' ? <AlertTriangle className="w-3 h-3" data-id="element-6476" data-mp="11h4oihcoswol" /> : <Combine className="w-3 h-3" data-id="element-6477" data-mp="qtoakx3bnb2o" />}
                  {didacticRelation.kind === 'pair' ? 'Paar' : didacticRelation.kind === 'tension' ? 'Botsend' : 'Cluster'}
                </span>}
              {transitional && <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-bold uppercase tracking-wider ${TRANSITIONAL_KIND_CONFIG[transitional.kind].badge}`} title={`Transitioneel / Sunset: ${transitional.label}. ${transitional.sunsetCondition}`} data-id="element-6478" data-mp="1ajgupuzvvgjs">
                  {TRANSITIONAL_KIND_CONFIG[transitional.kind].icon}
                  Sunset
                </span>}
            </div>
            <div className="flex items-center gap-2 text-xs" data-id="element-6479" data-mp="1y8t1ko7qncix">
              <span className={`px-1.5 py-0.5 rounded border text-[10px] font-bold uppercase tracking-wider ${dcolor}`} data-id="element-6480" data-mp="1yach2clkljjn">
                {entry.domain}
              </span>
              {!entry.isNeverFired && <span className={`flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-bold uppercase tracking-wider ${kcfg.bg} ${kcfg.color}`} data-id="element-6481" data-mp="2hd4x9zppxct3">
                  {kcfg.icon}
                  {entry.kind}
                </span>}
            </div>
          </div>

          {/* Right: Stats */}
          {!entry.isNeverFired && <div className="flex items-center gap-6 text-sm flex-shrink-0" data-id="element-6482" data-mp="1ymn6w1scytaw">
              <div className="text-right" data-id="element-6483" data-mp="1yku1epthef4h">
                <div className="text-gray-500 text-[10px] font-bold uppercase tracking-wider mb-0.5" data-id="element-6484" data-mp="1tnkbl926k1g4">
                  Emissies
                </div>
                <div className="font-mono font-medium text-gray-900" data-id="element-6485" data-mp="3a46es7gvvkfs">
                  {entry.count.toLocaleString()}
                </div>
              </div>
              <div className="text-right" data-id="element-6486" data-mp="1emwyokazxzcu">
                <div className="text-gray-500 text-[10px] font-bold uppercase tracking-wider mb-0.5" data-id="element-6487" data-mp="1eo82gk4fz73y">
                  Vragen
                </div>
                <div className="font-mono font-medium text-gray-900" data-id="element-6488" data-mp="1oj00fp1xrvz9">
                  {entry.questionsAffected.toLocaleString()}
                </div>
              </div>
              <div className="text-right" data-id="element-6489" data-mp="1xl0ukd1vi1wp">
                <div className="text-gray-500 text-[10px] font-bold uppercase tracking-wider mb-0.5" data-id="element-6490" data-mp="1xjy2w2wq7vfq">
                  Max Sev
                </div>
                <div className={`font-mono font-medium ${entry.maxSeverity >= 70 ? 'text-red-600' : entry.maxSeverity >= 30 ? 'text-amber-600' : 'text-blue-600'}`} data-id="element-6491" data-mp="27astfjh09h6z">
                  {entry.maxSeverity}
                </div>
              </div>
              <div className="pl-2 flex items-center" data-id="element-6492" data-mp="2g9e833jcwc2h">
                {expanded ? <ChevronDown className="w-5 h-5 text-gray-400" data-id="element-6493" data-mp="2laljrdomdvef" /> : <ChevronRight className="w-5 h-5 text-gray-400" data-id="element-6494" data-mp="9ojieo3rojcs" />}
              </div>
            </div>}
          {entry.isNeverFired && <div className="flex items-center pl-2" data-id="element-6495" data-mp="liix69epqi3">
              {expanded ? <ChevronDown className="w-5 h-5 text-gray-400" data-id="element-6496" data-mp="3rt4o53bry112" /> : <ChevronRight className="w-5 h-5 text-gray-400" data-id="element-6497" data-mp="sd83ba292ook" />}
            </div>}
        </div>

        {/* Lifecycle rationale (never-fired only) */}
        {lcfg && !expanded && <div className="mt-3 pt-3 border-t border-gray-100/50 flex items-start gap-2" data-id="element-6498" data-mp="jkmlbw6g0m0s">
            <span className="text-sm leading-none mt-0.5" data-id="element-6499" data-mp="embb3kc8k9i1">{lcfg.dot}</span>
            <p className="text-[11px] text-gray-500 leading-relaxed" data-id="element-6500" data-mp="ek8ozbe7f0il">
              {lifecycleBlurb}
            </p>
          </div>}
      </div>

      {/* Expanded Details */}
      {expanded && <div className="px-4 pb-4 pt-1 border-t border-gray-100/50 bg-gray-50/30" data-id="element-6501" data-mp="178w6cvvu15qv">
          {/* Description */}
          <div className="mb-4 flex items-start gap-2" data-id="element-6502" data-mp="1v5sqsuhv2sxp">
            <Info className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" data-id="element-6503" data-mp="1v5akt65ed0cl" />
            <p className="text-sm text-gray-700 leading-relaxed" data-id="element-6504" data-mp="1hdmc7cucwu0d">
              {description}
            </p>
          </div>

          {/* Critical Analysis */}
          {criticalAnalysis && <div className="mb-4 flex items-start gap-2 p-3 rounded-lg bg-amber-50/50 border border-amber-100" data-id="element-6505" data-mp="17fg7knh619rt">
              <Lightbulb className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" data-id="element-6506" data-mp="1cgdsv8xh2jxj" />
              <p className="text-xs text-amber-900 leading-relaxed" data-id="element-6507" data-mp="yguv9a2aeqzh">
                {criticalAnalysis}
              </p>
            </div>}

          {/* Didactic Lens — conceptuele relatie (paar / cluster / botsend) */}
          {didacticRelation && (() => {
        const isTension = didacticRelation.kind === 'tension';
        const tone = isTension ? {
          box: 'bg-red-50/50 border-red-100',
          icon: 'text-red-600',
          label: 'text-red-700',
          title: 'text-red-900',
          note: 'text-red-900',
          memberActive: 'bg-red-600 text-white border-red-600',
          memberIdle: 'bg-white text-red-700 border-red-200'
        } : {
          box: 'bg-indigo-50/50 border-indigo-100',
          icon: 'text-indigo-600',
          label: 'text-indigo-700',
          title: 'text-indigo-900',
          note: 'text-indigo-900',
          memberActive: 'bg-indigo-600 text-white border-indigo-600',
          memberIdle: 'bg-white text-indigo-700 border-indigo-200'
        };
        const heading = didacticRelation.kind === 'pair' ? 'Didactisch paar (vloer ↔ plafond)' : isTension ? 'Botsende codes (kunnen tegengesteld vuren)' : 'Didactisch cluster (verwant, niet samenvoegen)';
        return <div className={`mb-4 p-3 rounded-lg border ${tone.box}`} data-id="element-6508" data-mp="2ylxd4r81dwps">
                  <div className="flex items-center gap-2 mb-1.5 flex-wrap" data-id="element-6509" data-mp="2yo06nl8ee7z9">
                    {isTension ? <AlertTriangle className={`w-4 h-4 flex-shrink-0 ${tone.icon}`} data-id="element-6510" data-mp="26urb258r9oxv" /> : <Combine className={`w-4 h-4 flex-shrink-0 ${tone.icon}`} data-id="element-6511" data-mp="2awcxoib3dhl9" />}
                    <span className={`text-[10px] font-bold uppercase tracking-wider ${tone.label}`} data-id="element-6512" data-mp="2kuscpmwuh0ee">
                      {heading}
                    </span>
                    <span className={`text-[11px] font-medium ${tone.title}`} data-id="element-6513" data-mp="3id2bzmpa11">
                      {didacticRelation.label}
                    </span>
                  </div>
                  <p className={`text-xs leading-relaxed mb-2 ${tone.note}`} data-id="element-6514" data-mp="1fmzicb1xd721">
                    {didacticRelation.note}
                  </p>
                  <div className="flex flex-wrap gap-1.5" data-id="element-6515" data-mp="1auybj4nj08je">
                    {didacticRelation.members.map(m => <span key={m} className={`font-mono text-[10px] px-1.5 py-0.5 rounded border ${m === entry.code ? tone.memberActive : tone.memberIdle}`} data-id="element-6516" data-mp="15wwhjookaot5">
                        {m}
                      </span>)}
                  </div>
                </div>;
      })()}

          {/* Transitioneel / Sunset — migratie-teller of deprecatie */}
          {transitional && (() => {
        const tcfg = TRANSITIONAL_KIND_CONFIG[transitional.kind];
        const live = entry.count;
        const atZero = live === 0 && !entry.isNeverFired;
        return <div className="mb-4 p-3 rounded-lg border bg-violet-50/40 border-violet-100" data-id="element-6517" data-mp="1zc7o8ieg90ld">
                  <div className="flex items-center gap-2 mb-1.5 flex-wrap" data-id="element-6518" data-mp="1uiva527ecc6v">
                    <Clock className="w-4 h-4 flex-shrink-0 text-violet-600" data-id="element-6519" data-mp="1plv4utidfdgt" />
                    <span className="text-[10px] font-bold uppercase tracking-wider text-violet-700" data-id="element-6520" data-mp="1kmrxh2ex4v5o">
                      Transitioneel / Sunset
                    </span>
                    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-bold uppercase tracking-wider ${tcfg.badge}`} data-id="element-6521" data-mp="2daxhto228i4z">
                      {tcfg.icon}
                      {tcfg.label}
                    </span>
                    <span className="text-[11px] font-medium text-violet-900" data-id="element-6522" data-mp="ww6148ea0zbi">
                      {transitional.label}
                    </span>
                  </div>
                  <p className="text-xs leading-relaxed text-gray-700 mb-2" data-id="element-6523" data-mp="rzfndrpy2ft9">
                    <span className="font-semibold text-gray-800" data-id="element-6524" data-mp="33m9e3adswtcq">
                      Nu logisch:
                    </span>{' '}
                    {transitional.rationaleNow}
                  </p>
                  <p className="text-xs leading-relaxed text-gray-700 mb-2" data-id="element-6525" data-mp="3dh9q4aj5mtkt">
                    <span className="font-semibold text-gray-800" data-id="element-6526" data-mp="3iam8w415k65n">
                      Sunset-conditie:
                    </span>{' '}
                    {transitional.sunsetCondition}
                  </p>
                  <p className="text-xs leading-relaxed text-gray-700 mb-2" data-id="element-6527" data-mp="3r8xwdx6q2ntv">
                    <span className="font-semibold text-gray-800" data-id="element-6528" data-mp="3ogiycfzpxz">
                      Na de migratie:
                    </span>{' '}
                    {transitional.retirementGuidance}
                  </p>
                  {transitional.mergeTarget && <div className="flex items-center gap-1.5 text-[11px] mb-2" data-id="element-6529" data-mp="26ue4ry62omrv">
                      <Combine className="w-3.5 h-3.5 text-violet-500" data-id="element-6530" data-mp="26wi6qakevbpi" />
                      <span className="text-gray-500" data-id="element-6531" data-mp="1yuhli8y17q67">Kan opgaan in</span>
                      <span className="font-mono font-bold text-violet-700 bg-white border border-violet-200 rounded px-1.5 py-0.5" data-id="element-6532" data-mp="1uswcwzsj5y1t">
                        {transitional.mergeTarget}
                      </span>
                    </div>}
                  {!entry.isNeverFired && <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md border text-[11px] font-medium ${atZero ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`} data-id="element-6533" data-mp="2hrsi1lsa45hy">
                      {atZero ? <CheckCircle2 className="w-3.5 h-3.5" data-id="element-6534" data-mp="2mklm07fxb9sz" /> : <Clock className="w-3.5 h-3.5" data-id="element-6535" data-mp="31hmg2hll7gum" />}
                      {atZero ? 'Teller op 0 in deze run — sunset-kandidaat (bevestig over meerdere runs).' : `Nog ${live.toLocaleString()} emissies — migratie loopt nog.`}
                    </div>}
                </div>;
      })()}

          {/* Lifecycle rationale (never-fired only) - moved here when expanded */}
          {lcfg && <div className="mb-4 flex items-start gap-2 p-3 rounded-lg bg-white border border-gray-200" data-id="element-6536" data-mp="3b2vsymy0jfgi">
              <span className="text-sm leading-none mt-0.5" data-id="element-6537" data-mp="2wehibpucumpe">{lcfg.dot}</span>
              <p className="text-xs text-gray-600 leading-relaxed" data-id="element-6538" data-mp="316983y5osoeh">
                {lifecycleBlurb}
              </p>
            </div>}

          {/* Reason Breakdown (Rollups) */}
          {entry.reasonBreakdown && entry.reasonBreakdown.length > 0 && <div className="mb-4" data-id="element-6539" data-mp="2dgewfjx3t5vt">
              <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2" data-id="element-6540" data-mp="2dhg8nrpuvyf4">
                Onderverdeling (Rollup)
              </div>
              <div className="flex flex-wrap gap-2" data-id="element-6541" data-mp="3lqfg9di3vw4a">
                {entry.reasonBreakdown.map(rb => <div key={rb.reason} className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-white border border-gray-200 text-xs" data-id="element-6542" data-mp="3grknbiqfcr6g">
                    <span className="font-mono text-gray-600" data-id="element-6543" data-mp="371stiqq5g231">{rb.reason}</span>
                    <span className="text-gray-400" data-id="element-6544" data-mp="36zolbwl7wgwy">·</span>
                    <span className="font-medium text-gray-900" data-id="element-6545" data-mp="323zlrt97700n">
                      {rb.count}
                    </span>
                    <span className="text-gray-400 text-[10px]" data-id="element-6546" data-mp="2y2xkz7zmufwk">
                      ({rb.percentOfCode}%)
                    </span>
                  </div>)}
              </div>
            </div>}

          {/* Example Questions */}
          {!entry.isNeverFired && examples.length > 0 && <div className="mt-4" data-id="element-6547" data-mp="mbabtntlyaxh">
              <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-1.5" data-id="element-6548" data-mp="mcbr64hnpjl0">
                <MessageSquare className="w-3 h-3" data-id="element-6549" data-mp="tujcmso1t1ax" />
                Voorbeeld Vragen ({examples.length} van{' '}
                {entry.questionsAffected})
              </div>
              <div className="space-y-2" data-id="element-6550" data-mp="13lntxz174y8x">
                {examples.map((ex, idx) => <div key={`${ex.questionId}-${idx}`} className="bg-white border border-gray-200 rounded-lg p-3" data-id="element-6551" data-mp="18inww12r1kvt" data-mpi={idx}>
                    <div className="flex items-start justify-between gap-3 mb-2" data-id="element-6552" data-mp="fus0kxh3duzk">
                      <div className="min-w-0" data-id="element-6553" data-mp="ku4olljp983q">
                        <div className="flex items-center gap-2 mb-1" data-id="element-6554" data-mp="ktbqf8yt3qx7">
                          <span className="font-mono text-xs font-bold text-gray-900" data-id="element-6555" data-mp="ptr27w3fwqd2">
                            {ex.questionId}
                          </span>
                          <span className="text-[10px] text-gray-500 font-mono truncate" data-id="element-6556" data-mp="1wbea0dme2a7o">
                            {ex.familyId}
                          </span>
                        </div>
                        <p className="text-sm text-gray-700 leading-snug" data-id="element-6557" data-mp="188344vvi3u2">
                          {ex.issue.message}
                        </p>
                      </div>
                      <button onClick={e => {
                e.stopPropagation();
                setPreviewQuestionId(ex.questionId);
              }} className="flex-shrink-0 text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 px-2 py-1 rounded text-xs font-medium transition-colors flex items-center gap-1" data-id="element-6558" data-mp="a5ib9hde9cur">
                        <ExternalLink className="w-3 h-3" data-id="element-6559" data-mp="3nfg46uzrfoka" />
                        Bekijk
                      </button>
                    </div>

                    {/* Issue Details */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2 pt-2 border-t border-gray-100" data-id="element-6560" data-mp="2uv7tm4db3gs7">
                      {ex.issue.field && <div className="text-xs" data-id="element-6561" data-mp="2gr8raascxgjg">
                          <span className="text-gray-500" data-id="element-6562" data-mp="2gpflsythd2d1">Veld:</span>{' '}
                          <code className="bg-gray-100 px-1 rounded text-gray-800" data-id="element-6563" data-mp="2ljs598uyidos">
                            {ex.issue.field}
                          </code>
                        </div>}
                      {ex.issue.expected && <div className="text-xs" data-id="element-6564" data-mp="2zhgsgnchxwx0">
                          <span className="text-gray-500" data-id="element-6565" data-mp="2zirryaywsiib">Verwacht:</span>{' '}
                          <span className="text-gray-800" data-id="element-6566" data-mp="34fs330am8bga">
                            {ex.issue.expected}
                          </span>
                        </div>}
                      {ex.issue.actual && <div className="text-xs" data-id="element-6567" data-mp="2ukqdy4gcb9dp">
                          <span className="text-gray-500" data-id="element-6568" data-mp="1e3m0v6m1xdnn">Gevonden:</span>{' '}
                          <span className="text-red-700" data-id="element-6569" data-mp="1j49ttxtuc4ki">
                            {ex.issue.actual}
                          </span>
                        </div>}
                      {ex.issue.suggestion && <div className="text-xs sm:col-span-2" data-id="element-6570" data-mp="vcjngiidzbz9">
                          <span className="text-gray-500" data-id="element-6571" data-mp="qe9py196vzah">Suggestie:</span>{' '}
                          <span className="text-emerald-700 font-medium" data-id="element-6572" data-mp="mdptz10cr1px">
                            {ex.issue.suggestion}
                          </span>
                        </div>}
                    </div>
                  </div>)}
              </div>

              {/* Laad meer — toont de volgende batch voorbeelden tot alle
                                      getroffen vragen zichtbaar zijn. */}
              {hasMoreExamples && <button type="button" onClick={e => {
          e.stopPropagation();
          setVisibleExamples(n => n + EXAMPLE_PAGE_SIZE);
        }} className="mt-3 w-full flex items-center justify-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500" data-id="element-6573" data-mp="15ahh5lj0m0z9">
                  <ChevronDown className="w-3.5 h-3.5" data-id="element-6574" data-mp="3tqitbwynp9rk" />
                  Laad meer ({entry.questionsAffected - examples.length}{' '}
                  resterend)
                </button>}
            </div>}
        </div>}

      {/* Question Preview Modal */}
      <QuestionPreviewModal questionId={previewQuestionId} onClose={() => setPreviewQuestionId(null)} data-id="element-6575" data-mp="c9rijrjov554" />
    </Card>;
}
// ─── Question Preview Modal ───────────────────────────────────────────────────
/**
 * Dialog die een volledige vraag-preview toont. Volgt de huis-conventie voor
 * modals (zie o.a. ValidationErrorModal, ModulePanel, StudentDetailPanel):
 *   - useEscapeKey   → Esc sluit
 *   - useFocusTrap   → Tab blijft binnen de dialog + focus keert terug
 *   - useBodyScrollLock → achtergrond scrollt niet mee
 *   - backdrop-klik sluit, role="dialog" + aria-modal + aria-labelledby
 * Rendert niets als er geen vraag geselecteerd is (hooks draaien dan inert via
 * hun enabled/isActive-vlag, dus de hook-volgorde blijft stabiel).
 */
function QuestionPreviewModal({
  questionId,
  onClose
}: {
  questionId: string | null;
  onClose: () => void;
}) {
  const open = questionId !== null;
  const focusTrapRef = useFocusTrap(open);
  useEscapeKey(onClose, open);
  useBodyScrollLock(open);
  if (!open) return null;
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 bg-gray-900/50 backdrop-blur-sm" onClick={onClose} data-id="element-6576" data-mp="20i30bqd8zf3f">
      <div ref={focusTrapRef} role="dialog" aria-modal="true" aria-labelledby="code-registry-preview-title" className="bg-white rounded-2xl shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-6577" data-mp="2t6qgx8n1xgan">
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50" data-id="element-6578" data-mp="33bqfhky09ptj">
          <h3 id="code-registry-preview-title" className="font-bold text-gray-900 flex items-center gap-2 min-w-0" data-id="element-6579" data-mp="3h350fxbh4138">
            <Code2 className="w-4 h-4 text-gray-400 flex-shrink-0" data-id="element-6580" data-mp="3c5ljuqrazc84" />
            <span className="flex-shrink-0" data-id="element-6581" data-mp="2oepjv6cp1aps">Vraag Preview:</span>{' '}
            <span className="font-mono text-blue-600 truncate" data-id="element-6582" data-mp="2kcuki7ebp7en">
              {questionId}
            </span>
          </h3>
          <button type="button" onClick={onClose} aria-label="Preview sluiten" className="flex-shrink-0 text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-200 transition-colors focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none" data-id="element-6583" data-mp="2yd5g1nqkva65">
            <X className="w-5 h-5" data-id="element-6584" data-mp="8mjyv1l68aa5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto" data-id="element-6585" data-mp="gmg6ftjunifz">
          <QuestionPreviewPanel questionId={questionId} showBackOnMobile={false} data-id="element-6586" data-mp="gpu5osx7vbj7" />
        </div>
      </div>
    </div>;
}
// ─── Lifecycle Filter Chip ────────────────────────────────────────────────────
function LifecycleChip({
  active,
  onClick,
  label,
  tone = 'gray'
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  tone?: 'gray' | 'emerald' | 'amber' | 'red';
}) {
  const activeTone: Record<string, string> = {
    gray: 'bg-gray-900 text-white border-gray-900',
    emerald: 'bg-emerald-600 text-white border-emerald-600',
    amber: 'bg-amber-500 text-white border-amber-500',
    red: 'bg-red-600 text-white border-red-600'
  };
  return <button type="button" onClick={onClick} className={`text-xs font-medium px-3 py-1.5 rounded-lg border transition-colors focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none ${active ? activeTone[tone] : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50 hover:border-gray-300'}`} aria-pressed={active} data-id="element-6587" data-mp="bq7roh9kdr9c">
      {label}
    </button>;
}
// ─── Rollup Candidates View ───────────────────────────────────────────────────
/**
 * Read-only ANALYSE-laag: laat de codes zien die "hetzelfde doen" en — net als
 * de al-bestaande rollups — onder één canonieke code met `metadata.reason`
 * gevouwen kunnen worden. Voert GEEN merge uit; het maakt de kandidaten
 * zichtbaar voor de shadow-first stap (audit §3).
 */
function RollupCandidatesView({
  liveCountFor
}: {
  liveCountFor: (code: string) => number;
}) {
  const totalMembers = ROLLUP_CANDIDATE_FAMILIES.reduce((n, f) => n + f.memberCodes.length, 0);
  return <div className="space-y-4" data-id="element-6588" data-mp="2a4408i57g33z">
      {/* Explainer banner */}
      <Card className="p-4 bg-blue-50/40 border-blue-100" data-id="element-6589" data-mp="255u2bzs3ioeo">
        <div className="flex items-start gap-3" data-id="element-6590" data-mp="215sasjg2rbd4">
          <Combine className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" data-id="element-6591" data-mp="21623iesi7wmi" />
          <div className="min-w-0" data-id="element-6592" data-mp="2pvwoeof9thq6">
            <h3 className="text-sm font-bold text-gray-900" data-id="element-6593" data-mp="2pwy79f2j38ig">
              {ROLLUP_CANDIDATE_FAMILIES.length} kandidaat-families ·{' '}
              {totalMembers} codes
            </h3>
            <p className="text-[13px] text-gray-600 mt-1 leading-relaxed" data-id="element-6594" data-mp="2h3csvwu9zt9k">
              Codes die in essentie hetzelfde controleren, kunnen — net als de
              al-bestaande rollups (
              <span className="font-mono text-[11px]" data-id="element-6595" data-mp="274wzfn7s07ww">CONTENT_CITO_ISSUE</span>,{' '}
              <span className="font-mono text-[11px]" data-id="element-6596" data-mp="2c5t3q1w1hw2u">
                DIVERSITY_TYPE_OVERUSE
              </span>
              , …) — samen onder één canonieke code met een{' '}
              <span className="font-mono text-[11px]" data-id="element-6597" data-mp="2262as4n4np4v">metadata.reason</span>
              -onderverdeling. Dit is een{' '}
              <span className="font-medium text-gray-800" data-id="element-6598" data-mp="3skclyuog6c4n">
                voorstel-catalogus
              </span>
              , geen uitgevoerde merge: conform de audit (§3) gebeurt een merge
              pas ná grep op elke emit-call, een shadow-run met dubbel-emit, en
              bewezen 100% overlap — in series van 5–7.
            </p>
          </div>
        </div>
      </Card>

      {ROLLUP_CANDIDATE_FAMILIES.map(fam => <RollupFamilyCard key={fam.id} family={fam} liveCountFor={liveCountFor} data-id="element-6599" data-mp="1up5ot435et09" />)}
    </div>;
}
function RollupFamilyCard({
  family,
  liveCountFor
}: {
  family: RollupCandidateFamily;
  liveCountFor: (code: string) => number;
}) {
  const ccfg = ROLLUP_CONFIDENCE_CONFIG[family.confidence];
  const liveMembers = family.memberCodes.filter(c => liveCountFor(c) > 0).length;
  return <Card className="p-4" data-id="element-6600" data-mp="28mlz4qokf6xa">
      {/* Header: confidence + proposed canonical code */}
      <div className="flex items-start justify-between gap-3 flex-wrap" data-id="element-6601" data-mp="j7nzh9mbdvx9">
        <div className="min-w-0" data-id="element-6602" data-mp="j9quk82bqf8u">
          <div className="flex items-center gap-2 flex-wrap mb-1" data-id="element-6603" data-mp="e9mdogxi7ii3">
            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-bold uppercase tracking-wider ${ccfg.badge}`} data-id="element-6604" data-mp="eb753xoiulbq">
              {ccfg.icon}
              {ccfg.label}
            </span>
            <h3 className="text-sm font-bold text-gray-900" data-id="element-6605" data-mp="2o8gumvg0r67i">{family.title}</h3>
          </div>
          <div className="flex items-center gap-2 text-xs text-gray-500" data-id="element-6606" data-mp="2sbws1tzwxal9">
            <span className="font-medium text-gray-700" data-id="element-6607" data-mp="2xagdzueiamdm">
              {family.memberCodes.length} codes
            </span>
            <span className="text-gray-300" data-id="element-6608" data-mp="327zs8ub7ez5k">·</span>
            <span data-id="element-6609" data-mp="36zrhn1imiyu4">
              {liveMembers} vuren nu, {family.memberCodes.length - liveMembers}{' '}
              stil
            </span>
          </div>
        </div>
        <div className="flex items-center gap-1.5 text-xs flex-shrink-0" data-id="element-6610" data-mp="3by1dzfg345hb">
          <span className="text-gray-400" data-id="element-6611" data-mp="3g1hckhbpsfwn">→</span>
          <span className="font-mono font-bold text-blue-700 bg-blue-50 border border-blue-200 rounded px-1.5 py-0.5 text-[11px]" data-id="element-6612" data-mp="3kzircq23xt2b">
            {family.proposedCode}
          </span>
        </div>
      </div>

      {/* Shared concern */}
      <p className="text-[13px] text-gray-600 mt-3 leading-relaxed" data-id="element-6613" data-mp="14ct57wjmcwew">
        {family.sharedConcern}
      </p>

      {/* Member codes folding into the canonical */}
      <div className="mt-3" data-id="element-6614" data-mp="vce9e3913yx5">
        <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2" data-id="element-6615" data-mp="vd66jii9y4pj">
          Vouwt samen ({family.memberCodes.length})
        </div>
        <div className="flex flex-wrap gap-1.5" data-id="element-6616" data-mp="1s4hut55236s5">
          {family.memberCodes.map(code => {
          const count = liveCountFor(code);
          const fires = count > 0;
          return <span key={code} className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md border text-[11px] font-mono ${fires ? 'bg-white border-gray-200 text-gray-700' : 'bg-gray-50 border-gray-100 text-gray-400'}`} title={fires ? `${count} emissies in de huidige run` : 'Nooit getriggerd in de huidige run'} data-id="element-6617" data-mp="1j80hbv97ywt8">
                {code}
                <span className={`text-[9px] font-sans font-bold ${fires ? 'text-emerald-600' : 'text-gray-300'}`} data-id="element-6618" data-mp="1k4e7lsuskcr5">
                  {fires ? count.toLocaleString() : '0'}
                </span>
              </span>;
        })}
        </div>
      </div>

      {/* Proposed discriminator */}
      <div className="mt-3 pt-3 border-t border-gray-100" data-id="element-6619" data-mp="231p2705hai09">
        <div className="flex items-start gap-2" data-id="element-6620" data-mp="2315su7tw91xu">
          <ArrowRight className="w-3.5 h-3.5 text-gray-400 flex-shrink-0 mt-0.5" data-id="element-6621" data-mp="1p2phg3l36n9d" />
          <div className="min-w-0" data-id="element-6622" data-mp="1t3hnknynwc1f">
            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider" data-id="element-6623" data-mp="1t2gcijhnbpjp">
              Onderverdeling (Rollup)
            </span>
            <p className="text-[11px] font-mono text-gray-600 mt-0.5 break-words" data-id="element-6624" data-mp="homav7jkskni">
              {family.reasonDiscriminator}
            </p>
          </div>
        </div>
      </div>

      {/* Caveat — the honest "why not yet" */}
      <div className="mt-3 flex items-start gap-2 rounded-lg bg-gray-50/70 border border-gray-100 p-2.5" data-id="element-6625" data-mp="56s0vdrwo09h">
        <span className="text-sm leading-none mt-0.5" data-id="element-6626" data-mp="k27yttvlwgb5">{ccfg.dot}</span>
        <p className="text-[11px] text-gray-500 leading-relaxed" data-id="element-6627" data-mp="f4fwe5xc5bsp">
          {family.caveat}
        </p>
      </div>
    </Card>;
}
// ─── Didactic Relations View (Paren / Clusters / Botsingen) ──────────────────
/**
 * Read-only ANALYSE-laag die ALLE didactische relaties op één vindbare plek
 * toont — de "welke zijn logisch / botsend / dubbel"-vraag van de edtech/PO-
 * beoordelaar. Voorheen leefde de relatie alleen als badge op de losse
 * code-kaart; bij never-fired codes (of zonder run) was de 🔴-"Botsend"-badge
 * daardoor nauwelijks te vinden. Deze tab maakt paren, clusters én botsingen
 * altijd zichtbaar, los van of de codes in de huidige run vuren.
 */
function DidacticRelationsView({
  liveCountFor
}: {
  liveCountFor: (code: string) => number;
}) {
  const tensions = DIDACTIC_RELATIONS.filter(r => r.kind === 'tension');
  const pairs = DIDACTIC_RELATIONS.filter(r => r.kind === 'pair');
  const clusters = DIDACTIC_RELATIONS.filter(r => r.kind === 'cluster');
  return <div className="space-y-5" data-id="element-6628" data-mp="16nw7tgog7bna">
      {/* Explainer banner */}
      <Card className="p-4 bg-indigo-50/40 border-indigo-100" data-id="element-6629" data-mp="16mbetvhs80rj">
        <div className="flex items-start gap-3" data-id="element-6630" data-mp="ry74besvuvju">
          <Combine className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" data-id="element-6631" data-mp="rxf75zjn0prg" />
          <div className="min-w-0" data-id="element-6632" data-mp="wr9o4v7uyqmu">
            <h3 className="text-sm font-bold text-gray-900" data-id="element-6633" data-mp="iyjy8fp7l1kg">
              {DIDACTIC_RELATIONS.length} didactische relaties ·{' '}
              <span className="text-red-700" data-id="element-6634" data-mp="n16xh1o7loro">{tensions.length} botsend</span>,{' '}
              {pairs.length} paren, {clusters.length} clusters
            </h3>
            <p className="text-[13px] text-gray-600 mt-1 leading-relaxed" data-id="element-6635" data-mp="dzh5nuqf0ntd">
              De conceptuele lens over de codes:{' '}
              <span className="font-medium" data-id="element-6636" data-mp="3qi9ve9ncxvpb">paren</span> (vloer ↔ plafond op
              dezelfde as), <span className="font-medium" data-id="element-6637" data-mp="3lki0d6rf1tgw">clusters</span>{' '}
              (thematisch verwant, niet samenvoegen) en{' '}
              <span className="font-medium text-red-700" data-id="element-6638" data-mp="4cmov95sbtwy">botsingen</span> (codes
              die op dezelfde vraag tegengesteld kunnen trekken). Read-only —
              maakt zichtbaar welke codes logisch samenhangen en welke spanning
              dragen, zonder pipeline-gedrag te wijzigen.
            </p>
          </div>
        </div>
      </Card>

      {/* Botsingen first — that is the surface the reviewer was looking for */}
      {tensions.length > 0 && <div className="space-y-3" data-id="element-6639" data-mp="i2q43g68yncw">
          <div className="flex items-center gap-2" data-id="element-6640" data-mp="i4ktp7sgr3nb">
            <AlertTriangle className="w-4 h-4 text-red-600" data-id="element-6641" data-mp="w2rg9rryph7o" />
            <h3 className="text-sm font-bold text-gray-900" data-id="element-6642" data-mp="w3amib892h5v">
              Botsende codes ({tensions.length})
            </h3>
          </div>
          {tensions.map(rel => <DidacticRelationCard key={rel.label} relation={rel} liveCountFor={liveCountFor} data-id="element-6643" data-mp="z5ym1vfhv1o6" />)}
        </div>}

      {pairs.length > 0 && <div className="space-y-3" data-id="element-6644" data-mp="1918noi1zd71f">
          <div className="flex items-center gap-2" data-id="element-6645" data-mp="1dtjqbso3v6xo">
            <Combine className="w-4 h-4 text-indigo-600" data-id="element-6646" data-mp="1dv36lj9rhhzg" />
            <h3 className="text-sm font-bold text-gray-900" data-id="element-6647" data-mp="1hvn3qmuc4lll">
              Didactische paren ({pairs.length})
            </h3>
          </div>
          {pairs.map(rel => <DidacticRelationCard key={rel.label} relation={rel} liveCountFor={liveCountFor} data-id="element-6648" data-mp="1mw1a80gafnhi" />)}
        </div>}

      {clusters.length > 0 && <div className="space-y-3" data-id="element-6649" data-mp="3tkxuxtulqg7m">
          <div className="flex items-center gap-2" data-id="element-6650" data-mp="3tlzgwtd5on44">
            <Combine className="w-4 h-4 text-indigo-600" data-id="element-6651" data-mp="2ed9qqr8a2ii" />
            <h3 className="text-sm font-bold text-gray-900" data-id="element-6652" data-mp="2ewo5xho68rs">
              Thematische clusters ({clusters.length})
            </h3>
          </div>
          {clusters.map(rel => <DidacticRelationCard key={rel.label} relation={rel} liveCountFor={liveCountFor} data-id="element-6653" data-mp="2ax4pd39706ue" />)}
        </div>}
    </div>;
}
function DidacticRelationCard({
  relation,
  liveCountFor
}: {
  relation: DidacticRelation;
  liveCountFor: (code: string) => number;
}) {
  const isTension = relation.kind === 'tension';
  const tone = isTension ? {
    box: 'border-red-200 bg-red-50/40',
    icon: 'text-red-600',
    chip: 'bg-red-100 text-red-700 border-red-200',
    label: 'Botsend'
  } : {
    box: 'border-indigo-100 bg-white',
    icon: 'text-indigo-600',
    chip: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    label: relation.kind === 'pair' ? 'Paar' : 'Cluster'
  };
  return <Card className={`p-4 border ${tone.box}`} data-id="element-6654" data-mp="196zmtlzav9qk">
      <div className="flex items-center gap-2 flex-wrap mb-2" data-id="element-6655" data-mp="2x5iizbbcb3nv">
        {isTension ? <AlertTriangle className={`w-4 h-4 flex-shrink-0 ${tone.icon}`} data-id="element-6656" data-mp="31vyz536jp3dx" /> : <Combine className={`w-4 h-4 flex-shrink-0 ${tone.icon}`} data-id="element-6657" data-mp="31xtm0n1dn5kn" />}
        <span className={`inline-flex items-center px-1.5 py-0.5 rounded border text-[10px] font-bold uppercase tracking-wider ${tone.chip}`} data-id="element-6658" data-mp="2o6eyq40fsi7s">
          {tone.label}
        </span>
        <h4 className="text-sm font-bold text-gray-900" data-id="element-6659" data-mp="3gu10r7u0nesj">{relation.label}</h4>
      </div>
      <p className="text-[13px] text-gray-600 leading-relaxed mb-3" data-id="element-6660" data-mp="3kub92r5a6hak">
        {relation.note}
      </p>
      <div className="flex flex-wrap gap-1.5" data-id="element-6661" data-mp="36uk2hbh3g36l">
        {relation.members.map(code => {
        const count = liveCountFor(code);
        const fires = count > 0;
        return <span key={code} className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md border text-[11px] font-mono ${fires ? 'bg-white border-gray-200 text-gray-700' : 'bg-gray-50 border-gray-100 text-gray-400'}`} title={fires ? `${count} emissies in de huidige run` : 'Nooit getriggerd in de huidige run'} data-id="element-6662" data-mp="1vebsiozc1pkw">
              {code}
              <span className={`text-[9px] font-sans font-bold ${fires ? 'text-emerald-600' : 'text-gray-300'}`} data-id="element-6663" data-mp="1wwk9d6xy78y9">
                {fires ? count.toLocaleString() : '0'}
              </span>
            </span>;
      })}
      </div>
    </Card>;
}
// ─── Transitioneel / Sunset View ──────────────────────────────────────────────
/**
 * Read-only CURATIE-laag voor de "oude codes als EN_LEGACY"-vraag: codes die NU
 * logisch zijn maar hun meerwaarde verliezen zodra een eindige migratie/
 * opschoning af is. De live-teller is het sunset-signaal: groter dan 0 =
 * migratie loopt; stabiel 0 = retirement-kandidaat. Voert geen retire/merge uit
 * — het maakt de status plan-baar (grep + snapshot-scan vóór verwijderen).
 */
function TransitionalSunsetView({
  liveCountFor,
  hasRun
}: {
  liveCountFor: (code: string) => number;
  hasRun: boolean;
}) {
  const counters = TRANSITIONAL_CODES.filter(t => t.kind !== 'deprecation').length;
  const deprecations = TRANSITIONAL_CODES.length - counters;
  // Hoeveel staan in deze run al op 0 (alleen betekenisvol mét een run).
  const atZero = hasRun ? TRANSITIONAL_CODES.filter(t => liveCountFor(t.code) === 0).length : 0;
  return <div className="space-y-4" data-id="element-6664" data-mp="27kx3q7hmlxb1">
      {/* Explainer banner */}
      <Card className="p-4 bg-violet-50/40 border-violet-100" data-id="element-6665" data-mp="2ciykumvhrmjv">
        <div className="flex items-start gap-3" data-id="element-6666" data-mp="2clukcfmzmjaf">
          <Clock className="w-5 h-5 text-violet-600 flex-shrink-0 mt-0.5" data-id="element-6667" data-mp="2gm4ornv4t1n6" />
          <div className="min-w-0" data-id="element-6668" data-mp="3nywxt1tsisqd">
            <h3 className="text-sm font-bold text-gray-900" data-id="element-6669" data-mp="3nxvde6qvwtvz">
              {TRANSITIONAL_CODES.length} transitionele codes · {counters}{' '}
              migratie/auto-correctie-tellers, {deprecations} deprecaties
            </h3>
            <p className="text-[13px] text-gray-600 mt-1 leading-relaxed" data-id="element-6670" data-mp="1gezo55fts38q">
              "Oude" codes zoals{' '}
              <span className="font-mono text-[11px]" data-id="element-6671" data-mp="17kl6wxbqiimz">
                PEDAGOGY_MISCONCEPTION_EN_LEGACY
              </span>{' '}
              zijn <span className="font-medium" data-id="element-6672" data-mp="12kfc58a64g0o">nu logisch</span>, maar
              verliezen hun meerwaarde zodra een eindige migratie of opschoning
              af is. De{' '}
              <span className="font-medium text-gray-800" data-id="element-6673" data-mp="snkdmpdwafme">live-teller</span> is
              het sunset-signaal: zolang hij vuurt loopt de migratie nog, staat
              hij (stabiel over meerdere runs) op 0 dan is de code een
              retirement- of merge-kandidaat.{' '}
              {hasRun ? <>
                  Nu op 0 in deze run:{' '}
                  <span className="font-semibold text-emerald-700 tabular-nums" data-id="element-6674" data-mp="2ixdya70m552i">
                    {atZero}
                  </span>{' '}
                  van {TRANSITIONAL_CODES.length}.
                </> : <span className="text-amber-700" data-id="element-6675" data-mp="1ruw496gf6en9">
                  Start een governance-run om de tellers (en dus de
                  sunset-rijpheid) te zien.
                </span>}{' '}
              Read-only — verandert geen pipeline-gedrag.
            </p>
          </div>
        </div>
      </Card>

      {TRANSITIONAL_CODES.map(t => <TransitionalCard key={t.code} item={t} liveCountFor={liveCountFor} hasRun={hasRun} data-id="element-6676" data-mp="146wdw7n0sq97" />)}
    </div>;
}
function TransitionalCard({
  item,
  liveCountFor,
  hasRun
}: {
  item: TransitionalCode;
  liveCountFor: (code: string) => number;
  hasRun: boolean;
}) {
  const tcfg = TRANSITIONAL_KIND_CONFIG[item.kind];
  const live = liveCountFor(item.code);
  const atZero = hasRun && live === 0;
  return <Card className="p-4" data-id="element-6677" data-mp="2tiotinvi1pkd">
      {/* Header: kind + code + live status */}
      <div className="flex items-start justify-between gap-3 flex-wrap" data-id="element-6678" data-mp="hvv0gyk66zxq">
        <div className="min-w-0" data-id="element-6679" data-mp="hu1px86frvkg">
          <div className="flex items-center gap-2 flex-wrap mb-1" data-id="element-6680" data-mp="cy2yfbpt9xft">
            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-bold uppercase tracking-wider ${tcfg.badge}`} data-id="element-6681" data-mp="rm7iaj0lof06">
              {tcfg.icon}
              {tcfg.label}
            </span>
            <h3 className="text-sm font-bold text-gray-900" data-id="element-6682" data-mp="10lksxf0gn9mh">{item.label}</h3>
          </div>
          <span className="font-mono text-[11px] font-bold text-gray-700 bg-gray-50 border border-gray-200 rounded px-1.5 py-0.5 break-all" data-id="element-6683" data-mp="vmri5uck5wq0">
            {item.code}
          </span>
        </div>
        <div className="flex-shrink-0" data-id="element-6684" data-mp="15n0hhh1ujy9m">
          {hasRun ? <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md border text-[11px] font-medium ${atZero ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`} data-id="element-6685" data-mp="3cc5a0nxff642">
              {atZero ? <CheckCircle2 className="w-3.5 h-3.5" data-id="element-6686" data-mp="15z9hhxzc17cp" /> : <Clock className="w-3.5 h-3.5" data-id="element-6687" data-mp="10z3wh0nkij3l" />}
              {atZero ? 'Teller op 0 — sunset-kandidaat' : `${live.toLocaleString()} emissies — migratie loopt`}
            </span> : <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md border border-gray-200 bg-gray-50 text-gray-400 text-[11px] font-medium" data-id="element-6688" data-mp="n6nn7i42pou8">
              <Ghost className="w-3.5 h-3.5" data-id="element-6689" data-mp="i6j89witd264" />
              Nog niet gemeten
            </span>}
        </div>
      </div>

      {/* Nu logisch */}
      <p className="text-[13px] text-gray-600 mt-3 leading-relaxed" data-id="element-6690" data-mp="97xueeoaebbo">
        <span className="font-semibold text-gray-800" data-id="element-6691" data-mp="27mschfmc3oyb">Nu logisch:</span>{' '}
        {item.rationaleNow}
      </p>

      {/* Sunset-conditie */}
      <div className="mt-3 flex items-start gap-2 rounded-lg bg-violet-50/50 border border-violet-100 p-2.5" data-id="element-6692" data-mp="23miasf5krkp9">
        <Clock className="w-3.5 h-3.5 text-violet-500 flex-shrink-0 mt-0.5" data-id="element-6693" data-mp="3liaij4gx0eji" />
        <p className="text-[11px] text-violet-900 leading-relaxed" data-id="element-6694" data-mp="3qgv9edaa1lva">
          <span className="font-bold uppercase tracking-wider text-[10px] text-violet-700" data-id="element-6695" data-mp="3v8vfyokurd0u">
            Sunset-conditie
          </span>
          <br data-id="element-6696" data-mp="33j631mtw79f" />
          {item.sunsetCondition}
        </p>
      </div>

      {/* Na de migratie */}
      <div className="mt-2 flex items-start gap-2 rounded-lg bg-gray-50/70 border border-gray-100 p-2.5" data-id="element-6697" data-mp="32l9bzf7pq6dg">
        <ArrowRight className="w-3.5 h-3.5 text-gray-400 flex-shrink-0 mt-0.5" data-id="element-6698" data-mp="37jkmls5u0kwq" />
        <p className="text-[11px] text-gray-600 leading-relaxed" data-id="element-6699" data-mp="3bjkzv9h72q27">
          <span className="font-bold uppercase tracking-wider text-[10px] text-gray-400" data-id="element-6700" data-mp="3gjh5fd5jipjv">
            Na de migratie
          </span>
          <br data-id="element-6701" data-mp="qtdjplez1zuq" />
          {item.retirementGuidance}
        </p>
      </div>

      {/* Merge-doel */}
      {item.mergeTarget && <div className="mt-2 flex items-center gap-1.5 text-[11px]" data-id="element-6702" data-mp="vrngg0gch8ig">
          <Combine className="w-3.5 h-3.5 text-violet-500" data-id="element-6703" data-mp="2rvlaq5zalu5c" />
          <span className="text-gray-500" data-id="element-6704" data-mp="2nvb915ij9pwa">Kan opgaan in</span>
          <span className="font-mono font-bold text-violet-700 bg-violet-50 border border-violet-200 rounded px-1.5 py-0.5" data-id="element-6705" data-mp="2nurrvr0pj5jb">
            {item.mergeTarget}
          </span>
        </div>}
    </Card>;
}